import React, { useState, useEffect, useCallback, useRef } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { useInventoryBrand } from '../../hooks/useInventoryBrand';
import { excludeCompanyLevel } from '../../utils/brandFilters';
import {
  ProductCostsTab,
  ProductPricingTab,
  ProductBomTab,
  getProductDetail,
  saveProductRrp,
  endDateComponentBomLines
} from './ProductDetailTabs';
import ProductionBomEditorDialog from './ProductionBomEditorDialog';
import AddToRecipesDialog from './AddToRecipesDialog';
import { BomCatalogPanel } from './BomCatalog';
import bomService from '../../services/bomService';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TablePagination,
  Switch,
  FormControlLabel,
  Stack,
  TableSortLabel,
  Autocomplete,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  Layers as BundleIcon,
  Business as BusinessIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Link as LinkIcon,
  Warning as WarningIcon,
  SwapHoriz as SwapIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import EntityActionsMenu from '../common/EntityActionsMenu';
import BulkStatusUpdateDialog from './BulkStatusUpdateDialog';
import LeadTimeAvailabilityPanel from './LeadTimeAvailabilityPanel';
import BulkImportDialog from '../common/BulkImportDialog';
import useProductMasterBulkTools from './ProductMasterBulkTools';
import { cogsService } from '../../services/cogsService';
import { getCategories } from '../../services/categoryService';
import UnmappedSkuMappings from './UnmappedSkuMappings';
import AsinMapping from './AsinMapping';
import ReconciliationUnmappedSkus from './ReconciliationUnmappedSkus';
import MissingCOGS from './MissingCOGS';
import LeadTimeCell from './LeadTimeCell';
import LeadTimeOverrideDialog from './LeadTimeOverrideDialog';
import { POPULAR_CURRENCIES } from '../../utils/currencies';
import { cogsWriteErrorMessage, skippedCostRows, skippedCostRowsWarning, withCostSkipWarning } from '../../utils/cogsWriteFeedback';
import {
  getProductCosts,
  createProductCost,
  updateProductCost,
  deleteProductCost,
  getBundles,
  createBundle,
  updateBundle,
  replaceBundleGeography,
  createOrUpdateCogs,
  getAliases,
  createAlias,
  deleteAlias,
  formatCurrency,
  formatNumber,
  get90DaySales,
  getWarehouseCounts
} from '../../services/inventoryService';
import api from '../../config/api';
import { useUrlState } from '../../hooks/useUrlState';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

// Absolute API base, matching the pattern used across inventoryService.
const PRODUCT_API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Lightweight active/inactive toggle for a product OR bundle. Hits a dedicated
// endpoint that only updates product_master.is_active — no COGS or component (BOM)
// validation — so a bundle can be retired regardless of component quirks.
const setProductActiveStatus = (brandId, sku, isActive) =>
  api.patch(
    `${PRODUCT_API_BASE}/api/cogs/brands/${brandId}/products/${encodeURIComponent(sku)}/active`,
    { is_active: isActive ? 1 : 0 }
  ).then((r) => r.data);

function ProductCOGS() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedBrand, brands, selectBrand } = useInventoryBrand();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Products → Fill manufacturers / Export / Update from CSV (Rishabh 2026-10-23). loadData is
  // defined further down; the arrow only calls it at click time, after render.
  const productTools = useProductMasterBulkTools({ selectedBrand, onChanged: () => loadData(), setSuccess, setError });
  // Non-blocking: a save succeeded but the server skipped some cost rows (cogs_skipped, W20).
  const [warning, setWarning] = useState(null);

  // Bulk-actions state — COGS is brand-scoped, all actions require selectedBrand.
  const [cogsImportOpen, setCogsImportOpen] = useState(false);
  const [cogsImportMode, setCogsImportMode] = useState('insert');
  // Bundles bulk-import (parent_sku → component_sku, quantity rows)
  const [bundleImportOpen, setBundleImportOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);

  // Read initial values from URL query parameters
  const initialTab = parseInt(searchParams.get('tab') || '0', 10);
  const initialOrderBy = searchParams.get('orderBy') || 'sku';
  const initialOrder = searchParams.get('order') || 'asc';
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialRowsPerPage = parseInt(searchParams.get('rows') || '25', 10);
  const initialBrandId = searchParams.get('brand') ? parseInt(searchParams.get('brand'), 10) : null;
  // When ?product=<sku> (or ?product=new) is present, the component renders the
  // product view/edit as a FULL PAGE instead of the list — a real navigable URL,
  // not an overlay/modal (Rishabh 2026-07-02). Read fresh each render (reactive).
  const productParam = searchParams.get('product');

  const [activeTab, setActiveTab] = useState(initialTab >= 0 && initialTab <= 8 ? initialTab : 0);

  // Product-page tabs (Details | Costs | Pricing | BOM) — Rishabh 2026-07-02.
  // productDetail is the one-call aggregate (costs/rrp/tiers/boms) behind the
  // three read tabs; loaded whenever the full page opens on an existing SKU.
  const [productPageTab, setProductPageTab] = useState(0);
  const [productDetail, setProductDetail] = useState(null);
  const [productDetailLoading, setProductDetailLoading] = useState(false);
  const [productRefreshError, setProductRefreshError] = useState(null); // last refreshProductDetail failed (DT-2018)
  const [savingRrp, setSavingRrp] = useState(false);
  // Bulk "end this component's use in all BOMs" (Rishabh 2026-07-03) — offered
  // when a product is inactive yet still consumed by active recipes.
  const [endBomOpen, setEndBomOpen] = useState(false);
  const [endingBom, setEndingBom] = useState(false);

  // Data
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [sales90d, setSales90d] = useState({}); // { sku: units_sold_90d }
  // Brand category tree: [{ category, product_count, sub_categories: [{ sub_category, product_count }] }]
  const [categoryTree, setCategoryTree] = useState([]);
  // Optional list filters (product tabs only)
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  // { sku: { warehouse_count, incoming_qty, open_po_qty } } — stock signals from the stock engine
  const [stockSignals, setStockSignals] = useState({});
  // Default ON: hide SKUs with no stock anywhere, nothing incoming, and no open POs
  const [hideUnstocked, setHideUnstocked] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  // URL-backed so the filter survives opening a product and navigating back (#7).
  const [statusFilter, setStatusFilter] = useUrlState('status', 'all'); // 'all' | 'active' | 'inactive'
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Sorting
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [order, setOrder] = useState(initialOrder);

  // Helper to update URL parameters
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 0 || value === '0' || value === 'sku' && key === 'orderBy' || value === 'asc' && key === 'order' || value === 25 && key === 'rows') {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // Track if we've initialized from URL (to prevent overwriting URL on mount)
  const initializedFromUrl = useRef(false);

  // Sync brand from URL on mount - URL takes priority over context
  useEffect(() => {
    if (initialBrandId && brands.length > 0 && !initializedFromUrl.current) {
      const brandFromUrl = brands.find(b => b.id === initialBrandId);
      if (brandFromUrl) {
        initializedFromUrl.current = true;
        selectBrand(brandFromUrl);
      }
    }
  }, [initialBrandId, brands, selectBrand]);

  // Debounced search URL update
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPage(0);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce URL update by 500ms
    searchTimeoutRef.current = setTimeout(() => {
      updateUrlParams({ search: value, page: 0 });
    }, 500);
  }, []);
  
  // Dialogs
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [showBundleDetails, setShowBundleDetails] = useState(false);
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  // Lead-time override dialog: { open, sku, regime, computed, hasOverride }
  const [ltOverrideDialog, setLtOverrideDialog] = useState({ open: false, sku: '', regime: 'restock', computed: null, hasOverride: false });
  // Post-create quick-assign prompt (components only): { sku, name } | null
  const [assignRecipesFor, setAssignRecipesFor] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingBundle, setEditingBundle] = useState(null);
  const [editingAlias, setEditingAlias] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({
    sku: '',
    product_name: '',
    unit_cost: '',
    currency: 'GBP',
    supplier: '',
    lead_time_days: '',
    reorder_point: '',
    gtin: '',
    asin: '',
    weight: '',
    weight_unit: 'kg',
    hs_code: '',
    // Dimensions
    length_cm: '', width_cm: '', height_cm: '', dimension_unit: 'cm',
    // Compliance
    country_of_origin: '',
    is_hazmat: false, hazmat_class: '',
    // Procurement
    vendor: '', manufacturer: '', manufacturer_part_number: '',
    moq: '', case_pack: '',
    stock_uom: 'units',
    warehouse_shipping_cost: '',
    inbound_freight_cost: '',
    notes: '',
    category: '',
    sub_category: '',
    is_active: true,
    is_bundle: false,
    is_prepacked: false,
    bom_level: 1,
    apply_to_all_warehouses: true,
    warehouse_specific_costs: [],
    components: []
  });
  
  // Geography options for bundle components
  const GEOGRAPHY_OPTIONS = ['ALL', 'US', 'UK', 'EU', 'AU', 'SG', 'MY', 'CA'];

  const [bundleForm, setBundleForm] = useState({
    bundle_sku: '',
    bundle_name: '',
    gtin: '',
    weight: '',
    weight_unit: 'kg',
    hs_code: '',
    lead_time_days: '',
    // Dimensions
    length_cm: '', width_cm: '', height_cm: '', dimension_unit: 'cm',
    // Compliance
    country_of_origin: '',
    is_hazmat: false, hazmat_class: '',
    // Procurement
    vendor: '', manufacturer: '', manufacturer_part_number: '',
    moq: '', case_pack: '',
    is_prepacked: false,
    is_active: true,
    bundle_overhead: '',
    selectedGeography: 'ALL', // Currently selected geography tab
    componentsByGeography: {
      ALL: [{ component_sku: '', quantity: '' }]
    }
  });

  // Replace dialog state
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceForm, setReplaceForm] = useState({
    end_date: new Date().toISOString().split('T')[0],
    replacing_sku: '', // The component SKU being replaced
    replacing_quantity: '', // Original quantity
    new_sku: '', // New replacement SKU
    new_quantity: '' // New quantity (defaults to same)
  });

  const [aliasForm, setAliasForm] = useState({
    sku: '',
    primary_sku: ''
  });

  useEffect(() => {
    if (selectedBrand?.id) {
      loadData();
    }
  }, [selectedBrand?.id]);

  // Load the brand's category tree when the brand changes (best-effort — a
  // failure here must not block the products list). Reset filters on switch.
  useEffect(() => {
    if (!selectedBrand?.id) {
      setCategoryTree([]);
      return;
    }
    let cancelled = false;
    setCategoryFilter('');
    setSubCategoryFilter('');
    (async () => {
      try {
        const res = await getCategories(selectedBrand.id);
        if (!cancelled) setCategoryTree(res?.categories || []);
      } catch (err) {
        console.error('Failed to load category tree:', err);
        if (!cancelled) setCategoryTree([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBrand?.id]);

  // === COGS bulk actions =================================================

  // Export uses the existing in-memory build (downloadCogsCSV) wrapped to trigger
  // a browser download. Brand context required.
  const handleExportCogs = async () => {
    if (!selectedBrand?.id) return;
    try {
      const r = await cogsService.exportCogsCsv(selectedBrand.id);
      setSuccess(`Exported ${r.filename}`);
    } catch (e) {
      setError(apiErrorMessage(e, 'COGS export failed'));
    }
  };

  // Import: BulkImportDialog parses CSV/Excel client-side, hands us structured
  // rows. We re-stringify + post to the existing /bulk-import endpoint. Update
  // mode uses the same path — the backend's upsert (sku + warehouse_code) handles
  // both create and update naturally, so no separate update endpoint needed.
  const handleCogsBulkImport = async (rows) => {
    if (!selectedBrand?.id) throw new Error('Select a brand first');
    const result = await cogsService.bulkImportCogsRows(selectedBrand.id, rows);
    await loadData();
    return withCostSkipWarning(result, 'Import finished'); // cogs_skipped -> dialog warning (W20b)
  };

  // === Bundles bulk actions =================================================

  const handleExportBundles = async () => {
    if (!selectedBrand?.id) return;
    try {
      const res = await fetch(`/api/cogs/brands/${selectedBrand.id}/bundles/export.csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
      const blob = await res.blob();
      let filename = `bundles-brand-${selectedBrand.id}-${new Date().toISOString().slice(0, 10)}.csv`;
      const cd = res.headers.get('content-disposition');
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m) filename = m[1];
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess(`Exported ${filename}`);
    } catch (e) {
      setError(e.message || 'Bundle export failed');
    }
  };

  const handleBundleBulkImport = async (rows) => {
    if (!selectedBrand?.id) throw new Error('Select a brand first');
    const res = await fetch(`/api/cogs/brands/${selectedBrand.id}/bundles/bulk-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ rows }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || `HTTP ${res.status}`);
    await loadData();
    return withCostSkipWarning(result, 'Import finished'); // cogs_skipped -> dialog warning (W20b)
  };

  const BUNDLE_IMPORT_COLUMNS = [
    { field: 'parent_sku', label: 'Parent SKU', required: true,
      aliases: ['bundle_sku', 'parent', 'sku'] },
    { field: 'component_sku', label: 'Component SKU', required: true,
      aliases: ['component', 'child_sku', 'part_sku'] },
    { field: 'quantity', label: 'Quantity', required: true, type: 'number',
      aliases: ['qty', 'count'] },
    { field: 'currency', label: 'Currency', aliases: ['ccy'],
      help: 'Bundle-level. Only the first row of each parent_sku group is used.' },
    { field: 'bundle_overhead', label: 'Bundle overhead', type: 'number',
      help: 'Extra cost on top of component sum (packaging, kitting labour).' },
    { field: 'warehouse_code', label: 'Warehouse code',
      help: '"default" applies to all warehouses unless overridden per row.' },
  ];

  const BUNDLE_IMPORT_SAMPLE = [
    { parent_sku: 'SHIRT-3PACK-BLK', component_sku: 'SHIRT-BLACK-M', quantity: 1,
      currency: 'USD', bundle_overhead: 2.00, warehouse_code: 'default' },
    { parent_sku: 'SHIRT-3PACK-BLK', component_sku: 'SHIRT-BLACK-L', quantity: 1 },
    { parent_sku: 'SHIRT-3PACK-BLK', component_sku: 'SHIRT-BLACK-XL', quantity: 1 },
    { parent_sku: 'MASK-DUO-PINK-GREEN', component_sku: 'SS-FACE-50ML', quantity: 1,
      currency: 'GBP', bundle_overhead: 1.50 },
    { parent_sku: 'MASK-DUO-PINK-GREEN', component_sku: 'SS-GREEN-50ML', quantity: 1 },
  ];

  // Column spec — matches the existing /bulk-import endpoint's expectations.
  // bom_level: 0=raw_material/part, 1=finished_good, 2=bundle
  const COGS_IMPORT_COLUMNS = [
    { field: 'sku', label: 'SKU', required: true,
      aliases: ['product_sku', 'code'] },
    { field: 'warehouse_code', label: 'Warehouse code',
      aliases: ['warehouse', 'wh'],
      help: 'Use "default" for the brand-wide COGS; per-warehouse codes override per receipt.' },
    { field: 'cogs', label: 'COGS', required: true, type: 'number',
      aliases: ['cost', 'unit_cost', 'cogs_per_unit'] },
    { field: 'currency', label: 'Currency', aliases: ['ccy'] },
    { field: 'cogs_type', label: 'COGS type',
      enum: ['simple', 'bundle'],
      aliases: ['type'] },
    { field: 'sku_type', label: 'SKU type',
      enum: ['raw_material', 'finished_good', 'service'],
      aliases: ['kind'] },
    { field: 'bom_level', label: 'BOM level', type: 'number',
      help: '0 = raw material / part, 1 = finished good (made from parts), 2 = bundle (made from finished goods)',
      enum: ['0', '1', '2'] },
    { field: 'bundle_overhead', label: 'Bundle overhead', type: 'number',
      help: 'Extra cost on top of component sum (packaging, kitting labour).' },
    { field: 'warehouse_shipping_cost', label: 'Warehouse shipping cost', type: 'number' },
    { field: 'inbound_freight_cost', label: 'Inbound freight cost', type: 'number' },
    { field: 'effective_from', label: 'Effective from',
      help: 'YYYY-MM-DD. Defaults to today if blank.' },
    { field: 'notes', label: 'Notes' },
    { field: 'gtin', label: 'GTIN', aliases: ['ean', 'upc'] },
    { field: 'asin', label: 'ASIN' },
  ];

  const COGS_IMPORT_SAMPLE = [
    { sku: 'FABRIC-001', warehouse_code: 'default', cogs: 5.00, currency: 'USD',
      cogs_type: 'simple', sku_type: 'raw_material', bom_level: 0,
      bundle_overhead: 0, warehouse_shipping_cost: 0, inbound_freight_cost: 0,
      notes: 'Raw material — used to make shirts' },
    { sku: 'SHIRT-BLACK-M', warehouse_code: 'default', cogs: 5.75, currency: 'USD',
      cogs_type: 'simple', sku_type: 'finished_good', bom_level: 1,
      bundle_overhead: 0, warehouse_shipping_cost: 1.50, inbound_freight_cost: 0.50 },
    { sku: 'SHIRT-3PACK-BLK', warehouse_code: 'default', cogs: 19.50, currency: 'USD',
      cogs_type: 'bundle', sku_type: 'finished_good', bom_level: 2,
      bundle_overhead: 2.00, warehouse_shipping_cost: 2.00, inbound_freight_cost: 1.00 },
  ];

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
      updateUrlParams({ brand: brandId });
    }
  };

  const loadData = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const [productsRes, bundlesRes, aliasesRes, salesRes, warehouseRes] = await Promise.all([
        getProductCosts(selectedBrand.id, { limit: 10000, active_only: 'false' }),
        getBundles(selectedBrand.id, { limit: 10000, active_only: 'false' }),
        getAliases(selectedBrand.id),
        get90DaySales(selectedBrand.id),
        // Resilient: a warehouse-count failure must not break the whole page
        getWarehouseCounts(selectedBrand.id).catch(() => ({ data: [] }))
      ]);

      setProducts(productsRes?.data || []);
      setBundles(bundlesRes?.data || []);
      setAliases(aliasesRes?.data || []);

      // Convert sales array to lookup map: { sku: units_sold_90d }
      const salesMap = {};
      (salesRes?.data || []).forEach(s => {
        salesMap[s.sku] = s.units_sold_90d || 0;
      });
      setSales90d(salesMap);

      // Convert stock-signals array to lookup map: { sku: { warehouse_count, incoming_qty, open_po_qty } }
      const signalsMap = {};
      (warehouseRes?.data || []).forEach(w => {
        signalsMap[w.sku] = {
          warehouse_count: Number(w.warehouse_count) || 0,
          incoming_qty: Number(w.incoming_qty) || 0,
          open_po_qty: Number(w.open_po_qty) || 0,
          supplier: w.supplier || '',
          // [ [warehouse_code, qty], ... ] largest first
          warehouses: Array.isArray(w.warehouses) ? w.warehouses : []
        };
      });
      setStockSignals(signalsMap);
    } catch (err) {
      console.error('Failed to load product data:', err);
      setError('Failed to load product data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Group products by SKU (backend returns multiple rows per SKU if warehouse-specific COGS exist)
  // Calculate price range and track warehouse variants
  const groupedProducts = React.useMemo(() => {
    const skuMap = {};
    products.forEach(p => {
      const cost = parseFloat(p.calculated_cogs || p.cogs || p.unit_cost || 0);
      if (!skuMap[p.sku]) {
        skuMap[p.sku] = {
          ...p,
          warehouse_variants: [],
          min_cost: cost,
          max_cost: cost,
          warehouses: []
        };
      }
      // Track min/max costs
      if (cost > 0) {
        skuMap[p.sku].min_cost = Math.min(skuMap[p.sku].min_cost, cost);
        skuMap[p.sku].max_cost = Math.max(skuMap[p.sku].max_cost, cost);
      }
      // Track all warehouse variants for this SKU
      if (p.warehouse_code) {
        skuMap[p.sku].warehouse_variants.push(p);
        skuMap[p.sku].warehouses.push(p.warehouse_code);
      }
    });
    return Object.values(skuMap);
  }, [products]);

  // Helper to get product info by SKU (for displaying images in bundle components)
  const getProductBySku = (sku) => {
    return groupedProducts.find(p => p.sku === sku);
  };

  // Helper to format price range
  const formatPriceRange = (product) => {
    // Owner directive 2026-08-20: multi-warehouse costs display as a min–max
    // RANGE (backend now sends cost_min/cost_max/cost_warehouse_count), never
    // the max/an arbitrary warehouse alone.
    const min = parseFloat(product.cost_min || 0);
    const max = parseFloat(product.cost_max || 0);
    const nWh = parseInt(product.cost_warehouse_count, 10) || 0;
    if (nWh > 1 && max - min > 0.005) {
      return `${formatCurrency(min)} – ${formatCurrency(max)}`;
    }
    if (max > 0) return formatCurrency(max);
    // No range rows and no fallback cost in the payload: last-resort fields.
    return formatCurrency(parseFloat(product.calculated_cogs || product.cogs || product.unit_cost || 0));
  };
  
  // Effective external PRODUCTION lead time (days) — the real replenishment lead for these
  // products (they're replenished via overseas production runs; external fast-restocks barely
  // exist). Mirrors LeadTimeCell precedence: override > SKU-actual (when it has runs) > brand
  // estimate > manual value. All external (intercompany excluded upstream in getCogs).
  const productionLeadDays = (p) => {
    const ovrP = p.override_production_lead_time_days;
    if (ovrP !== null && ovrP !== undefined && ovrP !== '') return parseInt(ovrP) || 0;
    const pN = parseInt(p.production_sample_size) || 0;
    if (pN > 0) return parseInt(p.production_lead_time_days) || 0;
    const pBrand = parseInt(p.brand_production_lead_time_days) || 0;
    if (pBrand > 0) return pBrand;
    return parseInt(p.lead_time_days) || 0;
  };
  // Reorder point = expected demand during the external replenishment lead time
  //   = production lead (days) × daily sales velocity (units sold in last 90d / 90).
  // Null when we lack either a lead time or any recent sales.
  const reorderPoint = (p) => {
    const lead = productionLeadDays(p);
    const units90 = sales90d[p.sku] || 0;
    if (!lead || !units90) return null;
    return Math.round(lead * units90 / 90);
  };

  // A SKU is "live" if it has stock in any warehouse, incoming transfers, or open POs
  const hasStockSignal = (sku) => {
    const s = stockSignals[sku];
    return !!s && (s.warehouse_count > 0 || s.incoming_qty > 0 || s.open_po_qty > 0);
  };
  // Default ON: hide dead SKUs (no stock anywhere, nothing incoming, no open POs).
  // Fail-open: if signals haven't loaded yet or the endpoint failed (empty map), hide nothing.
  const applyStockFilter = (list) => {
    if (!hideUnstocked || Object.keys(stockSignals).length === 0) return list;
    return list.filter(p => hasStockSignal(p.sku));
  };

  // Filter products by BOM Level for the 3 tabs
  const partsList = applyStockFilter(groupedProducts.filter(p => p.bom_level === 0));
  const productsList = applyStockFilter(groupedProducts.filter(p => p.bom_level === 1));
  // Bundles are VIRTUAL (assembled from components): no direct warehouse stock,
  // nothing "incoming", and you never raise a PO for the bundle itself, so
  // hasStockSignal() is false for essentially all of them. Applying the
  // "hide unstocked / dead SKUs" filter here wrongly hid every bundle except the
  // odd one carrying a signal (SandandSky showed 1 of 106). A bundle's
  // availability is its components' stock, not its own on-hand — the Bundles tab
  // must NOT be subject to applyStockFilter. (Rishabh 2026-09-15)
  const bundlesList = groupedProducts.filter(p => p.bom_level === 2);
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    updateUrlParams({ orderBy: property, order: newOrder, page: 0 });
    setPage(0);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal, bVal;
    
    if (orderBy === 'unit_cost') {
      aVal = parseFloat(a.calculated_cogs || a.cogs || a.unit_cost || 0);
      bVal = parseFloat(b.calculated_cogs || b.cogs || b.unit_cost || 0);
    } else if (orderBy === 'units_sold_90d') {
      aVal = sales90d[a.sku] || 0;
      bVal = sales90d[b.sku] || 0;
    } else if (orderBy === 'lead_time_days') {
      // Sort by production lead time (SKU-actual, else brand estimate), then restock, then manual
      const eff = (x) => parseFloat(x.production_lead_time_days) || parseFloat(x.brand_production_lead_time_days)
        || parseFloat(x.restock_lead_time_days) || parseFloat(x.brand_restock_lead_time_days) || parseFloat(x.lead_time_days) || 0;
      aVal = eff(a);
      bVal = eff(b);
    } else if (orderBy === 'reorder_point') {
      aVal = reorderPoint(a) || 0;
      bVal = reorderPoint(b) || 0;
    } else if (orderBy === 'supplier') {
      // Sort on the same value the Supplier cell shows (warehouse-signal supplier, else
      // the most-recent-PO supplier_name) so the column sorts by what's on screen.
      const sup = (x) => ((stockSignals[x.sku] && stockSignals[x.sku].supplier) || x.supplier_name || x.vendor || '').toLowerCase();
      aVal = sup(a);
      bVal = sup(b);
    } else {
      aVal = a[orderBy] || '';
      bVal = b[orderBy] || '';
    }
    
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
  };

  const getFilteredList = () => {
    if (activeTab === 3) return aliases; // Aliases handled separately

    let list = [];
    switch (activeTab) {
      case 0: list = productsList; break; // L1 - Finished Goods
      case 1: list = bundlesList; break;  // L2 - Bundles
      case 2: list = partsList; break;    // L0 - Parts
      default: list = productsList;
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      list = list.filter(p =>
        p.sku?.toLowerCase().includes(search) ||
        p.product_name?.toLowerCase().includes(search)
      );
    } else {
      // Gift cards are Shopify catalog rows kept for order tracking — no
      // costs, suppliers or BOMs to manage, so they only clutter browsing.
      // Hidden unless searched for ("gift" finds them) — same
      // search-overrides-hide convention as the Price Tiers list
      // (Rishabh 2026-07-06).
      list = list.filter(p => !String(p.product_name || '').toLowerCase().includes('gift card'));
    }

    if (statusFilter !== 'all') {
      const wantActive = statusFilter === 'active';
      list = list.filter(p => {
        const inactive = p.is_active === 0 || p.is_active === '0' || p.is_active === false;
        return wantActive ? !inactive : inactive;
      });
    }
    if (categoryFilter) {
      list = list.filter(p => (p.category || '') === categoryFilter);
    }
    if (subCategoryFilter) {
      list = list.filter(p => (p.sub_category || '') === subCategoryFilter);
    }

    // Sort the list
    return list.sort(getComparator(order, orderBy));
  };
  
  const currentList = getFilteredList();

  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      // If product has warehouse variants, load them for editing
      const hasWarehouseVariants = product.warehouse_variants && product.warehouse_variants.length > 0;
      setProductForm({
        sku: product.sku,
        product_name: product.product_name || '',
        // Backend uses 'cogs' field, frontend displays as 'unit_cost'
        // For bundles, calculated_cogs includes component costs
        unit_cost: product.calculated_cogs || product.cogs || product.unit_cost || '',
        currency: product.currency || 'GBP',
        supplier: product.supplier || '',
        lead_time_days: product.lead_time_days || '',
        reorder_point: product.reorder_point || '',
        gtin: product.gtin || '',
        asin: product.asin || '',
        weight: product.weight_kg || '',
        weight_unit: product.weight_unit || 'kg',
        hs_code: product.hs_code || '',
        length_cm: product.length_cm || '', width_cm: product.width_cm || '', height_cm: product.height_cm || '',
        dimension_unit: product.dimension_unit || 'cm',
        country_of_origin: product.country_of_origin || '',
        is_hazmat: !!product.is_hazmat, hazmat_class: product.hazmat_class || '',
        vendor: product.vendor || '', manufacturer: product.manufacturer || '',
        manufacturer_part_number: product.manufacturer_part_number || '',
        moq: product.moq || '', case_pack: product.case_pack || '',
        stock_uom: product.stock_uom || 'units',
        warehouse_shipping_cost: product.warehouse_shipping_cost || '',
        inbound_freight_cost: product.inbound_freight_cost || '',
        notes: product.notes || '',
        category: product.category || '',
        sub_category: product.sub_category || '',
        is_active: product.is_active !== false && product.is_active !== 0,
        is_bundle: !!product.is_bundle,
        is_prepacked: !!product.is_prepacked,
        bom_level: product.bom_level ?? 1,
        apply_to_all_warehouses: !hasWarehouseVariants,
        warehouse_specific_costs: hasWarehouseVariants ? product.warehouse_variants.map(v => ({
          warehouse_code: v.warehouse_code,
          cogs: v.cogs || v.unit_cost || '',
          // Per-row source currency (Rishabh 2026-07-03) — falls back to the
          // product-level currency for legacy rows.
          currency: v.currency || product.currency || 'USD',
          supplier: v.supplier || ''
        })) : [],
        components: [] // Components managed via inline selector or Bundle Dialog
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        sku: '',
        product_name: '',
        unit_cost: '',
        currency: 'GBP',
        supplier: '',
        lead_time_days: '',
        reorder_point: '',
        gtin: '',
        asin: '',
        weight: '',
        weight_unit: 'kg',
        hs_code: '',
        length_cm: '', width_cm: '', height_cm: '', dimension_unit: 'cm',
        country_of_origin: '',
        is_hazmat: false, hazmat_class: '',
        vendor: '', manufacturer: '', manufacturer_part_number: '',
        moq: '', case_pack: '',
        stock_uom: 'units',
        warehouse_shipping_cost: '',
        inbound_freight_cost: '',
        notes: '',
        is_active: true,
        is_bundle: false,
        is_prepacked: false,
        bom_level: 1,
        apply_to_all_warehouses: true,
        warehouse_specific_costs: [],
        components: []
      });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    // Ensure brand is selected
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      return;
    }

    // Deactivation guardrail POPUP (Rishabh 2026-07-03: toggled a component
    // inactive and got no warning): deactivating does NOT remove it from BOMs.
    const wasActive = editingProduct
      ? !(editingProduct.is_active === 0 || editingProduct.is_active === '0' || editingProduct.is_active === false)
      : false;
    const bomUse = productDetail?.component_usage;
    if (editingProduct && wasActive && !productForm.is_active && bomUse?.lines > 0) {
      const ok = await confirm({
        title: 'Still used in recipes',
        message: `${editingProduct.sku} is a component in ${bomUse.lines} active BOM line${bomUse.lines > 1 ? 's' : ''} (${bomUse.finished_goods.length} finished good${bomUse.finished_goods.length > 1 ? 's' : ''}). Deactivating does NOT remove it from those recipes — future builds will still consume it. You can end its BOM use afterwards from the banner on this page.`,
        confirmText: 'Deactivate anyway',
        severity: 'warning'
      });
      if (!ok) return;
    }

    try {
      setError(null);
      setWarning(null);
      // Save responses that may carry cogs_skipped (POST cogs / createBundle).
      const saveResponses = [];

      // Category / sub_category are TOP-LEVEL on the COGS save payload (applied to
      // product_master). '' clears the field; we always send the dialog's current
      // values so an edit persists whatever the user selected.
      const categoryPayload = {
        category: productForm.category || '',
        sub_category: productForm.sub_category || ''
      };

      // Check if this is a bundle with valid components
      const validComponents = (productForm.components || []).filter(c => c.component_sku && c.quantity);
      const isBundleWithComponents = productForm.is_bundle && validComponents.length > 0;

      if (isBundleWithComponents) {
        // Bundle save flow: create/update bundle with components
        const bundleData = {
          bundle: {
            sku: productForm.sku,
            product_name: productForm.product_name,
            gtin: productForm.gtin || '',
            weight: parseFloat(productForm.weight) || 0,
            weight_unit: productForm.weight_unit || 'kg',
            hs_code: productForm.hs_code || '',
            lead_time_days: parseInt(productForm.lead_time_days) || 0,
            length_cm: parseFloat(productForm.length_cm) || 0, width_cm: parseFloat(productForm.width_cm) || 0,
            height_cm: parseFloat(productForm.height_cm) || 0, dimension_unit: productForm.dimension_unit || 'cm',
            country_of_origin: productForm.country_of_origin || '',
            is_hazmat: productForm.is_hazmat ? 1 : 0, hazmat_class: productForm.hazmat_class || '',
            vendor: productForm.vendor || '', manufacturer: productForm.manufacturer || '',
            manufacturer_part_number: productForm.manufacturer_part_number || '',
            moq: parseInt(productForm.moq) || 0, case_pack: parseInt(productForm.case_pack) || 0,
            is_bundle: 1,
            is_prepacked: productForm.is_prepacked ? 1 : 0,
            bundle_overhead: 0,
            cogs: 0 // Auto-calculated from components
          },
          components: validComponents.map(c => ({
            component_sku: c.component_sku,
            quantity: parseInt(c.quantity) || 1,
            geography: 'ALL'
          }))
        };

        if (editingProduct) {
          // Converting existing product to bundle: first update to mark as bundle, then set components
          const cogsData = {
            sku: productForm.sku,
            product_name: productForm.product_name,
            cogs: parseFloat(productForm.unit_cost) || 0,
            currency: productForm.currency,
            is_active: productForm.is_active ? 1 : 0,
            is_bundle: 1,
            is_prepacked: productForm.is_prepacked ? 1 : 0,
            bom_level: 2
          };
          await updateProductCost(selectedBrand.id, editingProduct.sku, { ...cogsData, ...categoryPayload });
          // Now add components to the newly-marked bundle
          await updateBundle(selectedBrand.id, productForm.sku, bundleData);
          setSuccess('Product converted to bundle with components');
        } else {
          // New bundle with components
          saveResponses.push(await createBundle(selectedBrand.id, bundleData));
          setSuccess('Bundle created with components');
        }
      } else if (editingProduct && productForm.warehouse_specific_costs.length > 0) {
        // If editing and has warehouse-specific costs, save each variant
        const cogsArray = productForm.warehouse_specific_costs.map(whCost => ({
          sku: productForm.sku,
          product_name: productForm.product_name,
          warehouse_code: whCost.warehouse_code,
          cogs: parseFloat(whCost.cogs) || 0,
          // Per-row currency wins; product-level currency is the fallback.
          currency: whCost.currency || productForm.currency,
          supplier: whCost.supplier || productForm.supplier,
          lead_time_days: parseInt(productForm.lead_time_days) || 0,
          reorder_point: parseInt(productForm.reorder_point) || 0,
          gtin: productForm.gtin,
          asin: productForm.asin,
          weight: parseFloat(productForm.weight) || 0,
          weight_unit: productForm.weight_unit || 'kg',
          hs_code: productForm.hs_code || '',
          length_cm: parseFloat(productForm.length_cm) || 0, width_cm: parseFloat(productForm.width_cm) || 0,
          height_cm: parseFloat(productForm.height_cm) || 0, dimension_unit: productForm.dimension_unit || 'cm',
          country_of_origin: productForm.country_of_origin || '',
          is_hazmat: productForm.is_hazmat ? 1 : 0, hazmat_class: productForm.hazmat_class || '',
          vendor: productForm.vendor || '', manufacturer: productForm.manufacturer || '',
          manufacturer_part_number: productForm.manufacturer_part_number || '',
          moq: parseInt(productForm.moq) || 0, case_pack: parseInt(productForm.case_pack) || 0,
          stock_uom: productForm.stock_uom || 'units',
          warehouse_shipping_cost: parseFloat(productForm.warehouse_shipping_cost) || 0,
          inbound_freight_cost: parseFloat(productForm.inbound_freight_cost) || 0,
          notes: productForm.notes,
          is_active: productForm.is_active ? 1 : 0,
          is_bundle: productForm.is_bundle ? 1 : 0,
          is_prepacked: productForm.is_prepacked ? 1 : 0,
          bom_level: productForm.bom_level ?? 1,
          sku_type: (productForm.bom_level ?? 1) === 0 ? 'raw_material' : 'finished_good'
        }));

        saveResponses.push(await createOrUpdateCogs(selectedBrand.id, cogsArray, categoryPayload));
        setSuccess(`Product updated for ${productForm.warehouse_specific_costs.length} warehouse(s)`);
      } else {
        // Single COGS entry (applies to all warehouses OR new product)
        const cogsData = {
          sku: productForm.sku,
          product_name: productForm.product_name,
          cogs: parseFloat(productForm.unit_cost) || 0,
          currency: productForm.currency,
          supplier: productForm.supplier,
          lead_time_days: parseInt(productForm.lead_time_days) || 0,
          reorder_point: parseInt(productForm.reorder_point) || 0,
          gtin: productForm.gtin,
          asin: productForm.asin,
          weight: parseFloat(productForm.weight) || 0,
          weight_unit: productForm.weight_unit || 'kg',
          hs_code: productForm.hs_code || '',
          length_cm: parseFloat(productForm.length_cm) || 0, width_cm: parseFloat(productForm.width_cm) || 0,
          height_cm: parseFloat(productForm.height_cm) || 0, dimension_unit: productForm.dimension_unit || 'cm',
          country_of_origin: productForm.country_of_origin || '',
          is_hazmat: productForm.is_hazmat ? 1 : 0, hazmat_class: productForm.hazmat_class || '',
          vendor: productForm.vendor || '', manufacturer: productForm.manufacturer || '',
          manufacturer_part_number: productForm.manufacturer_part_number || '',
          moq: parseInt(productForm.moq) || 0, case_pack: parseInt(productForm.case_pack) || 0,
          stock_uom: productForm.stock_uom || 'units',
          warehouse_shipping_cost: parseFloat(productForm.warehouse_shipping_cost) || 0,
          inbound_freight_cost: parseFloat(productForm.inbound_freight_cost) || 0,
          notes: productForm.notes,
          is_active: productForm.is_active ? 1 : 0,
          is_bundle: productForm.is_bundle ? 1 : 0,
          is_prepacked: productForm.is_prepacked ? 1 : 0,
          bom_level: productForm.bom_level ?? 1,
          sku_type: (productForm.bom_level ?? 1) === 0 ? 'raw_material' : 'finished_good'
        };

        if (editingProduct) {
          await updateProductCost(selectedBrand.id, editingProduct.sku, { ...cogsData, ...categoryPayload });
          setSuccess('Product updated successfully');
        } else {
          saveResponses.push(await createProductCost(selectedBrand.id, cogsData, categoryPayload));
          setSuccess('Product created successfully');
          // New COMPONENT: offer to put it straight into finished-goods
          // recipes (Rishabh 2026-07-03 — create-then-visit-every-FG was two
          // disconnected flows). Skippable; closing changes nothing.
          if ((productForm.bom_level ?? 1) === 0 && !productForm.is_bundle) {
            setAssignRecipesFor({ sku: productForm.sku, name: productForm.product_name });
          }
        }
      }

      setWarning(skippedCostRowsWarning(skippedCostRows(saveResponses), 'Saved'));
      closeProductPage();
      loadData();
    } catch (err) {
      setError(cogsWriteErrorMessage(err, 'Failed to save product'));
    }
  };

  // Navigation for the full-page product form (query-param routed, not a modal).
  // openProductPage receives the full list-row object (so a click populates the
  // form immediately) or the string 'new' for Add. closeProductPage returns to
  // the list by clearing the ?product param, preserving all other list filters.
  const openProductPage = (productOrNew) => {
    if (productOrNew === 'new') {
      handleOpenProductDialog();
    } else {
      handleOpenProductDialog(productOrNew);
    }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('product', productOrNew === 'new' ? 'new' : productOrNew.sku);
      return p;
    });
  };
  const closeProductPage = () => {
    setProductDialogOpen(false);
    setEditingProduct(null);
    // If we arrived from the Stock Levels page (via a SKU link with ?from=stock),
    // Back should return there — not to the Products & COGS list. Otherwise fall
    // back to the default: drop ?product= and show the list.
    if (searchParams.get('from') === 'stock') {
      // Restore the Stock Levels filters that were captured in ?back= on the way in.
      const back = searchParams.get('back');
      navigate('/inventory/stock' + (back ? `?${decodeURIComponent(back)}` : ''));
      return;
    }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('product');
      return p;
    }, { replace: true });
  };
  // Jump from the open product page to ANOTHER product's page (e.g. a finished
  // good from the "Used in" chips). Prefer the loaded list row so the form
  // populates immediately; otherwise just set ?product= and let the
  // direct-load effect's fallbacks (bundle shape / minimal shell) handle it.
  const [usedInAnchor, setUsedInAnchor] = useState(null);
  const jumpToProduct = (sku) => {
    const found = groupedProducts.find((p) => p.sku === sku);
    if (found) { openProductPage(found); return; }
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('product', sku);
      return p;
    });
  };

  // Direct-load / refresh support: when the URL already carries ?product=<sku>
  // (no prior click populated the form), find the row in the loaded lists once
  // they're available and populate the form. The editingProduct?.sku guard makes
  // this a no-op for the click-through case and prevents list-refresh from
  // clobbering the user's in-progress edits.
  useEffect(() => {
    if (!productParam || productParam === 'new') return;
    const decoded = decodeURIComponent(productParam);
    if (editingProduct && editingProduct.sku === decoded) return;
    // Search the UNFILTERED grouped list — the tab lists are stock-filtered
    // (hide-unstocked default), which made ?product=<sku> silently fall back
    // to Add-Product mode for SKUs with no stock signal (e.g. kits).
    const found = groupedProducts.find((p) => p.sku === decoded);
    if (found) { handleOpenProductDialog(found); return; }
    // Fallbacks so ?product=<sku> works for ANY sku the BOM catalog can name:
    // bundle-only SKUs (no product row) open as their bundle shape; otherwise a
    // minimal shell opens the page so the Costs/Pricing/BOM tabs (which load by
    // SKU) still work instead of silently falling into Add-Product mode.
    if (!products.length && !bundles.length) return; // lists not loaded yet
    const bundleRow = bundles.find((b) => b.bundle_sku === decoded);
    if (bundleRow) {
      handleOpenProductDialog({
        sku: bundleRow.bundle_sku,
        product_name: bundleRow.bundle_name || '',
        is_bundle: true,
        is_prepacked: bundleRow.is_prepacked,
        is_active: bundleRow.is_active,
        bundle_overhead: bundleRow.bundle_overhead,
        bom_level: 2,
      });
      return;
    }
    handleOpenProductDialog({ sku: decoded, product_name: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productParam, products, bundles]);

  // Load the Costs/Pricing/BOM aggregate for the open product page. Reset the
  // tab + data whenever the SKU changes so stale data never flashes.
  // ?ptab=N deep-links straight to a product-page tab (3 = BOM — the BOM
  // catalog navigates here instead of its old drill dialog).
  useEffect(() => {
    const ptab = parseInt(searchParams.get('ptab') || '0', 10);
    setProductPageTab(ptab >= 0 && ptab <= 3 ? ptab : 0);
    setProductDetail(null);
    setProductRefreshError(null); // DT-2018: it was about the page being left
    if (!productParam || productParam === 'new' || !selectedBrand?.id) return;
    const decoded = decodeURIComponent(productParam);
    let cancelled = false;
    setProductDetailLoading(true);
    getProductDetail(selectedBrand.id, decoded)
      .then((data) => { if (!cancelled) setProductDetail(data); })
      .catch((err) => { if (!cancelled) console.error('Product detail load failed:', err); })
      .finally(() => { if (!cancelled) setProductDetailLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productParam, selectedBrand?.id]);

  // --- BOM tab editing (production recipes + sales kits) ---------------------
  // Production BOMs use the shared ProductionBomEditorDialog (full-replacement
  // save via /api/boms production-boms). Sales kits reuse the EXISTING bundle
  // dialog so all bundle plumbing (per-geography components, compat view,
  // COGS recalc) stays on its proven write path.
  const [prodBomEditor, setProdBomEditor] = useState({ open: false, mode: 'create', initialForm: null });

  const refreshProductDetail = async () => {
    if (!productParam || productParam === 'new' || !selectedBrand?.id) return;
    setProductRefreshError(null);
    try {
      const fresh = await getProductDetail(selectedBrand.id, decodeURIComponent(productParam));
      setProductDetail(fresh);
    } catch (e) {
      console.error('Product detail refresh failed:', e);
      // DT-2012 sibling: runs after a production BOM save / deactivate and a bundle save; a failure used to leave
      // the product page stale with no message.
      // DT-2018: own state (not the shared page error), cleared as each refresh starts and when the product changes.
      setProductRefreshError('Could not refresh this product page, so it may be out of date. Reload the page to try again.');
    }
  };

  const handleEditProductionBom = async (bom) => {
    const sku = decodeURIComponent(productParam || '');
    if (!sku || !selectedBrand?.id) return;
    if (!bom) {
      // No production BOM yet — open the creator with the assembly locked.
      setProdBomEditor({ open: true, mode: 'create', initialForm: { assembly_sku: sku, components: [] } });
      return;
    }
    // Prefill from the BOM catalog drill (has unit + notes per component,
    // which the product-detail aggregate doesn't carry).
    try {
      const drill = await bomService.drillBom(selectedBrand.id, sku, { include_inactive: 1 });
      const prod = (drill.boms || []).find((b) => b.bom_type === 'production' && b.is_master_default)
        || (drill.boms || []).find((b) => b.bom_type === 'production');
      setProdBomEditor({
        open: true,
        mode: 'edit',
        initialForm: {
          assembly_sku: sku,
          name: prod?.name || '',
          production_lead_time_days: prod?.production_lead_time_days || '',
          components: (prod?.components || []).map((c) => ({
            sku: c.component_sku, quantity: c.quantity, unit: c.unit || 'units', notes: c.notes || '',
            is_active: c.is_active === 0 ? 0 : 1,
          })),
        },
      });
    } catch (e) {
      setError(apiErrorMessage(e, 'Failed to load BOM for editing'));
    }
  };

  const handleEditKit = () => {
    const sku = decodeURIComponent(productParam || '');
    if (!sku) return;
    const bundleData = bundles.find((b) => b.bundle_sku === sku);
    if (bundleData) {
      handleOpenBundleDialog(bundleData);
      return;
    }
    // Not in the bundles list (e.g. geo-scoped kit with no product row) —
    // NEVER open the full-replacement editor empty: prefill the per-geography
    // components from the recipe we're already displaying (productDetail.boms),
    // so an immediate Update round-trips instead of wiping the kit.
    const componentsByGeo = {};
    for (const bom of (productDetail?.boms || [])) {
      if (bom.bom_type === 'production') continue;
      const geos = (bom.geographies && bom.geographies.length) ? bom.geographies : ['ALL'];
      for (const geo of geos) {
        if (!componentsByGeo[geo]) componentsByGeo[geo] = [];
        for (const c of (bom.components || [])) {
          componentsByGeo[geo].push({ component_sku: c.component_sku, quantity: c.quantity });
        }
      }
    }
    const product = products.find((p) => p.sku === sku);
    handleOpenBundleDialog({
      bundle_sku: sku,
      bundle_name: product?.product_name || productDetail?.product_name || '',
      is_prepacked: product?.is_prepacked,
      is_active: product?.is_active,
      bundle_overhead: product?.bundle_overhead,
      components_by_geography: componentsByGeo,
    });
  };

  // Upsert per-geography RRP rows, then refresh the aggregate so the grid
  // reflects what the server actually stored.
  const handleSaveRrp = async (rows, onDone) => {
    if (!selectedBrand?.id || !productParam || productParam === 'new') return;
    const decoded = decodeURIComponent(productParam);
    try {
      setSavingRrp(true);
      setError(null);
      await saveProductRrp(selectedBrand.id, decoded, rows);
      const fresh = await getProductDetail(selectedBrand.id, decoded);
      setProductDetail(fresh);
      if (onDone) onDone();
      setSuccess('RRP saved');
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save RRP'));
    } finally {
      setSavingRrp(false);
    }
  };

  // Bulk end-date: stop every active recipe from consuming this component as
  // of today. History preserved (lines end-dated, not deleted); audited.
  const handleEndBomUse = async () => {
    if (!selectedBrand?.id || !productParam || productParam === 'new') return;
    const decoded = decodeURIComponent(productParam);
    try {
      setEndingBom(true);
      setError(null);
      const result = await endDateComponentBomLines(selectedBrand.id, decoded);
      setEndBomOpen(false);
      const fresh = await getProductDetail(selectedBrand.id, decoded);
      setProductDetail(fresh);
      setSuccess(`Ended use in ${result.lines_ended} BOM line${result.lines_ended > 1 ? 's' : ''} (${result.finished_goods.length} finished goods). Historical builds are unaffected.`);
      // Auto-dismiss — the global success bar otherwise follows the user to
      // the list and other products (Rishabh 2026-07-03).
      setTimeout(() => setSuccess((s) => (s && s.startsWith('Ended use in') ? null : s)), 8000);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to end BOM use'));
    } finally {
      setEndingBom(false);
    }
  };

  const handleDeleteProduct = async (sku) => {
    // Honest copy: there is no hard delete platform-wide — this closes the
    // SKU's cost records (all warehouses) and moves the product to Inactive.
    // Saying "deleted" while the row stayed visible under Status=All read as
    // "delete is broken" (Rishabh 2026-07-06).
    if (!await confirm({
      title: 'Delete product',
      message: `Delete ${sku}? Its cost records will be closed and the product moved to Inactive. History is kept — you can still find it under Status → Inactive.`,
      confirmText: 'Delete',
      severity: 'danger'
    })) return;
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      return;
    }

    try {
      await deleteProductCost(selectedBrand.id, sku);
      setSuccess(`${sku} deleted — cost records closed, product moved to Inactive`);
      // Make the row visibly leave: under "All" the deleted product would
      // still sit in the list with an Inactive chip.
      if (statusFilter === 'all') setStatusFilter('active');
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete product'));
    }
  };

  const handleOpenAliasDialog = (alias = null) => {
    if (alias) {
      setEditingAlias(alias);
      setAliasForm({
        sku: alias.sku,
        primary_sku: alias.primarysku
      });
    } else {
      setEditingAlias(null);
      setAliasForm({
        sku: '',
        primary_sku: ''
      });
    }
    setError(null); // the dialog now shows `error`; never carry a stale page error into it
    setAliasDialogOpen(true);
  };

  const handleSaveAlias = async () => {
    try {
      setError(null);
      await createAlias({
        sku: aliasForm.sku,
        primary_sku: aliasForm.primary_sku
      });
      setSuccess('Alias saved successfully');
      setAliasDialogOpen(false);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to save alias'));
    }
  };

  const handleDeleteAlias = async (sku) => {
    if (!await confirm({ title: 'Delete Alias', message: `Are you sure you want to delete alias for ${sku}?`, confirmText: 'Delete', severity: 'danger' })) return;
    try {
      await deleteAlias(sku);
      setSuccess('Alias deleted successfully');
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to delete alias'));
    }
  };

  const handleOpenBundleDialog = (bundle = null) => {
    if (bundle) {
      console.log('Opening bundle for edit:', bundle);
      console.log('Bundle components_by_geography:', bundle.components_by_geography);
      setEditingBundle(bundle);

      // Use components_by_geography if available, otherwise fall back to flat components
      let componentsByGeo = {};
      if (bundle.components_by_geography && Object.keys(bundle.components_by_geography).length > 0) {
        componentsByGeo = { ...bundle.components_by_geography };
      } else if (bundle.components && bundle.components.length > 0) {
        // Legacy format: group by geography
        bundle.components.forEach(c => {
          const geo = c.geography || 'ALL';
          if (!componentsByGeo[geo]) {
            componentsByGeo[geo] = [];
          }
          componentsByGeo[geo].push({
            component_sku: c.component_sku || '',
            quantity: c.quantity || '',
            effective_from: c.effective_from || '',
            effective_to: c.effective_to || ''
          });
        });
      }

      // Ensure at least ALL geography exists with empty component
      if (Object.keys(componentsByGeo).length === 0) {
        componentsByGeo = { ALL: [{ component_sku: '', quantity: '' }] };
      }

      // Determine initial geography tab (prefer first available, or ALL)
      const availableGeos = Object.keys(componentsByGeo);
      const initialGeo = availableGeos.includes('ALL') ? 'ALL' : availableGeos[0];

      setBundleForm({
        bundle_sku: bundle.bundle_sku,
        bundle_name: bundle.bundle_name || '',
        gtin: bundle.gtin || '',
        weight: bundle.weight_kg || '',
        weight_unit: bundle.weight_unit || 'kg',
        hs_code: bundle.hs_code || '',
        lead_time_days: bundle.lead_time_days || '',
        length_cm: bundle.length_cm || '', width_cm: bundle.width_cm || '', height_cm: bundle.height_cm || '',
        dimension_unit: bundle.dimension_unit || 'cm',
        country_of_origin: bundle.country_of_origin || '',
        is_hazmat: !!bundle.is_hazmat, hazmat_class: bundle.hazmat_class || '',
        vendor: bundle.vendor || '', manufacturer: bundle.manufacturer || '',
        manufacturer_part_number: bundle.manufacturer_part_number || '',
        moq: bundle.moq || '', case_pack: bundle.case_pack || '',
        is_prepacked: !!bundle.is_prepacked,
        is_active: bundle.is_active !== false && bundle.is_active !== 0,
        bundle_overhead: bundle.bundle_overhead || '',
        selectedGeography: initialGeo,
        componentsByGeography: componentsByGeo
      });
    } else {
      setEditingBundle(null);
      setBundleForm({
        bundle_sku: '',
        bundle_name: '',
        gtin: '',
        weight: '',
        weight_unit: 'kg',
        hs_code: '',
        lead_time_days: '',
        length_cm: '', width_cm: '', height_cm: '', dimension_unit: 'cm',
        country_of_origin: '',
        is_hazmat: false, hazmat_class: '',
        vendor: '', manufacturer: '', manufacturer_part_number: '',
        moq: '', case_pack: '',
        is_prepacked: false,
        is_active: true,
        bundle_overhead: '',
        selectedGeography: 'ALL',
        componentsByGeography: {
          ALL: [{ component_sku: '', quantity: '' }]
        }
      });
    }
    setError(null); // the dialog now shows `error`; never carry a stale page error into it
    setBundleDialogOpen(true);
  };

  const handleAddBundleComponent = () => {
    const geo = bundleForm.selectedGeography;
    const currentComponents = bundleForm.componentsByGeography[geo] || [];
    setBundleForm({
      ...bundleForm,
      componentsByGeography: {
        ...bundleForm.componentsByGeography,
        [geo]: [...currentComponents, { component_sku: '', quantity: '' }]
      }
    });
  };

  const handleRemoveBundleComponent = (index) => {
    const geo = bundleForm.selectedGeography;
    const currentComponents = bundleForm.componentsByGeography[geo] || [];
    const newComponents = currentComponents.filter((_, i) => i !== index);
    setBundleForm({
      ...bundleForm,
      componentsByGeography: {
        ...bundleForm.componentsByGeography,
        [geo]: newComponents.length > 0 ? newComponents : [{ component_sku: '', quantity: '' }]
      }
    });
  };

  const handleUpdateBundleComponent = (index, field, value) => {
    const geo = bundleForm.selectedGeography;
    const currentComponents = [...(bundleForm.componentsByGeography[geo] || [])];
    currentComponents[index] = { ...currentComponents[index], [field]: value };
    setBundleForm({
      ...bundleForm,
      componentsByGeography: {
        ...bundleForm.componentsByGeography,
        [geo]: currentComponents
      }
    });
  };

  const handleGeographyTabChange = (newGeo) => {
    // Ensure the geography has components array
    if (!bundleForm.componentsByGeography[newGeo]) {
      setBundleForm({
        ...bundleForm,
        selectedGeography: newGeo,
        componentsByGeography: {
          ...bundleForm.componentsByGeography,
          [newGeo]: [{ component_sku: '', quantity: '' }]
        }
      });
    } else {
      setBundleForm({
        ...bundleForm,
        selectedGeography: newGeo
      });
    }
  };

  const handleCopyComponentsToGeography = (targetGeo) => {
    const sourceGeo = bundleForm.selectedGeography;
    const sourceComponents = bundleForm.componentsByGeography[sourceGeo] || [];
    setBundleForm({
      ...bundleForm,
      componentsByGeography: {
        ...bundleForm.componentsByGeography,
        [targetGeo]: sourceComponents.map(c => ({ ...c }))
      }
    });
    setSuccess(`Copied components from ${sourceGeo} to ${targetGeo}`);
  };

  const handleOpenReplaceDialog = (component = null) => {
    setReplaceForm({
      end_date: new Date().toISOString().split('T')[0],
      replacing_sku: component?.component_sku || '',
      replacing_quantity: component?.quantity || '',
      new_sku: '',
      new_quantity: component?.quantity || ''
    });
    setError(null); setReplaceDialogOpen(true);
  };

  const handleReplaceGeography = async () => {
    if (!selectedBrand?.id || !editingBundle) return;

    try {
      setError(null);
      const geo = bundleForm.selectedGeography;

      await replaceBundleGeography(
        selectedBrand.id,
        editingBundle.bundle_sku,
        geo,
        {
          end_date: replaceForm.end_date,
          replacing_sku: replaceForm.replacing_sku, // Optional - only ends this specific SKU if provided
          new_components: [{
            component_sku: replaceForm.new_sku,
            quantity: parseInt(replaceForm.new_quantity) || 1
          }]
        }
      );

      const oldProduct = getProductBySku(replaceForm.replacing_sku);
      const newProduct = getProductBySku(replaceForm.new_sku);
      setSuccess(`Successfully replaced ${oldProduct?.product_name || replaceForm.replacing_sku} with ${newProduct?.product_name || replaceForm.new_sku} for ${geo}. Old component ends on ${replaceForm.end_date}, new component starts the next day.`);
      setReplaceDialogOpen(false);
      setBundleDialogOpen(false);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to replace component'));
    }
  };

  const handleSaveBundle = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand first');
      return;
    }

    try {
      setError(null);
      setWarning(null);

      // Flatten all geography components into a single array with geography field
      const allComponents = [];
      let hasInvalidQty = false;
      Object.entries(bundleForm.componentsByGeography).forEach(([geo, components]) => {
        components.forEach(c => {
          if (c.component_sku) {
            const qty = parseInt(c.quantity);
            if (!qty || qty < 1) {
              hasInvalidQty = true;
            } else {
              allComponents.push({
                component_sku: c.component_sku,
                quantity: qty,
                geography: geo
              });
            }
          }
        });
      });

      if (hasInvalidQty) {
        setError('All components must have a quantity of at least 1');
        return;
      }

      if (allComponents.length === 0) {
        setError('At least one component is required');
        return;
      }

      // Format bundle data for backend API
      const bundleData = {
        bundle: {
          sku: bundleForm.bundle_sku,
          product_name: bundleForm.bundle_name,
          gtin: bundleForm.gtin || '',
          weight: parseFloat(bundleForm.weight) || 0,
          weight_unit: bundleForm.weight_unit || 'kg',
          hs_code: bundleForm.hs_code || '',
          lead_time_days: parseInt(bundleForm.lead_time_days) || 0,
          length_cm: parseFloat(bundleForm.length_cm) || 0, width_cm: parseFloat(bundleForm.width_cm) || 0,
          height_cm: parseFloat(bundleForm.height_cm) || 0, dimension_unit: bundleForm.dimension_unit || 'cm',
          country_of_origin: bundleForm.country_of_origin || '',
          is_hazmat: bundleForm.is_hazmat ? 1 : 0, hazmat_class: bundleForm.hazmat_class || '',
          vendor: bundleForm.vendor || '', manufacturer: bundleForm.manufacturer || '',
          manufacturer_part_number: bundleForm.manufacturer_part_number || '',
          moq: parseInt(bundleForm.moq) || 0, case_pack: parseInt(bundleForm.case_pack) || 0,
          is_bundle: 1,
          is_prepacked: bundleForm.is_prepacked ? 1 : 0,
          // is_active is handled by the live status toggle (setProductActiveStatus),
          // not here — so the heavy bundle save never gates status on BOM validation.
          bundle_overhead: parseFloat(bundleForm.bundle_overhead) || 0,
          cogs: 0 // Will be calculated from components
        },
        components: allComponents
      };

      if (editingBundle) {
        // Update existing bundle
        await updateBundle(selectedBrand.id, bundleForm.bundle_sku, bundleData);
        setSuccess('Bundle updated successfully');
      } else {
        // Create new bundle
        const created = await createBundle(selectedBrand.id, bundleData);
        setSuccess('Bundle created successfully');
        setWarning(skippedCostRowsWarning(skippedCostRows(created), 'Bundle created'));
      }
      setBundleDialogOpen(false);
      loadData();
      // Keep the open product page's BOM tab in sync when the kit was edited
      // from there.
      refreshProductDetail();
    } catch (err) {
      setError(cogsWriteErrorMessage(err, 'Failed to save bundle'));
    }
  };

  // Calculate bundle cost from components
  const calculateBundleCost = (bundle) => {
    if (!bundle.components) return 0;
    return bundle.components.reduce((total, comp) => {
      const product = products.find(p => p.sku === comp.component_sku);
      const cost = parseFloat(product?.calculated_cogs || product?.cogs || product?.unit_cost || 0);
      const qty = parseFloat(comp.quantity || 0);
      return total + (cost * qty);
    }, 0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Category dropdown options for the edit dialog. Include the product's own
  // value if it isn't in the tree (legacy value) so the Select never renders blank.
  const dialogCategoryOptions = (() => {
    const opts = categoryTree.map(c => c.category);
    if (productForm.category && !opts.includes(productForm.category)) opts.push(productForm.category);
    return opts;
  })();
  const dialogSubCategoryOptions = (() => {
    const node = categoryTree.find(c => c.category === productForm.category);
    const opts = node ? node.sub_categories.map(s => s.sub_category) : [];
    if (productForm.sub_category && !opts.includes(productForm.sub_category)) opts.push(productForm.sub_category);
    return opts;
  })();
  // Sub-category options for the list filter (depend on the selected category filter).
  const filterSubOptions = (() => {
    const node = categoryTree.find(c => c.category === categoryFilter);
    return node ? node.sub_categories.map(s => s.sub_category) : [];
  })();

  return (
    <Box>
      {/* List view — hidden while the full-page product form is open (?product=…) */}
      {!productParam && (<>
      {/* Brand Selector Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Products & COGS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage product costs, suppliers, and bundles
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={selectedBrand?.id || ''}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                {excludeCompanyLevel(brands).map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (activeTab === 3) handleOpenAliasDialog();
                  else if (activeTab === 1) handleOpenBundleDialog(); // Bundles is now Tab 1
                  else if (activeTab === 8) return; // BOMs tab has its own New Production BOM button
                  else openProductPage('new'); // Products (0) or Parts (2) → full page
                }}
                disabled={!selectedBrand?.id}
              >
                {activeTab === 3 ? 'Add Alias' : activeTab === 1 ? 'Create Bundle' : 'Add Product'}
              </Button>
              <EntityActionsMenu
                label="Actions"
                buttonProps={{ disabled: !selectedBrand?.id }}
                actions={[
                  {
                    key: 'import-cogs',
                    label: 'Import COGS',
                    hint: 'CSV/Excel — sku, cogs, currency, bundle_overhead…',
                    icon: <ImportIcon fontSize="small" />,
                    group: 'COGS',
                    onClick: () => { setCogsImportMode('insert'); setCogsImportOpen(true); },
                  },
                  {
                    key: 'export-cogs',
                    label: 'Export current COGS',
                    hint: `Downloads ${selectedBrand?.name || 'brand'}'s full COGS as CSV`,
                    icon: <ExportIcon fontSize="small" />,
                    group: 'COGS',
                    onClick: handleExportCogs,
                  },
                  // No separate "Update COGS from CSV" — the /cogs/bulk-import
                  // endpoint UPSERTs on (sku, warehouse_code), so re-uploading
                  // an exported CSV via "Import COGS" updates existing rows in
                  // place. A separate update-mode would require an `id` column
                  // the export doesn't have, and would error every row.
                  {
                    key: 'bulk-status',
                    label: 'Bulk status update…',
                    hint: 'Export the status sheet, edit in Excel, re-import with a safe preview before anything changes.',
                    icon: <ImportIcon fontSize="small" />,
                    group: 'Status',
                    onClick: () => setBulkStatusOpen(true),
                  },
                  {
                    key: 'import-bundles',
                    label: 'Import bundles',
                    hint: 'CSV: one row per component. Columns: parent_sku, component_sku, quantity.',
                    icon: <ImportIcon fontSize="small" />,
                    group: 'Bundles',
                    onClick: () => setBundleImportOpen(true),
                  },
                  {
                    key: 'export-bundles',
                    label: 'Export bundles',
                    hint: 'Downloads all bundles for the brand — one row per component (round-trippable).',
                    icon: <ExportIcon fontSize="small" />,
                    group: 'Bundles',
                    onClick: handleExportBundles,
                  },
                  ...productTools.actions,
                ]}
              />
              <Tooltip title="Refresh data">
                <IconButton onClick={loadData} color="primary" disabled={!selectedBrand?.id}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Brand Selection Alert */}
      {!selectedBrand && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand above to view products and COGS data
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>
          {warning}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Total Parts (L0)</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{formatNumber(partsList.length)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CategoryIcon color="secondary" />
                <Typography variant="body2" color="textSecondary">Finished Goods (L1)</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{formatNumber(productsList.length)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BundleIcon color="success" />
                <Typography variant="body2" color="textSecondary">Bundles (L2)</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{formatNumber(bundlesList.length)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon color="warning" />
                <Typography variant="body2" color="textSecondary">Total Products</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatNumber(products.length)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => { setActiveTab(v); updateUrlParams({ tab: v, page: 0 }); setPage(0); }} variant="scrollable" scrollButtons="auto">
            <Tab label={`Products (${productsList.length})`} icon={<CategoryIcon />} iconPosition="start" />
            <Tab label={`Bundles (${bundlesList.length})`} icon={<BundleIcon />} iconPosition="start" />
            <Tab label={`Parts (${partsList.length})`} icon={<CategoryIcon />} iconPosition="start" />
            <Tab label={`Aliases (${aliases.length})`} icon={<BusinessIcon />} iconPosition="start" />
            <Tab label="SKU Mappings" icon={<WarningIcon />} iconPosition="start" />
            <Tab label="ASIN Mappings" icon={<LinkIcon />} iconPosition="start" />
            <Tab label="Batch Reconciliation" icon={<SwapIcon />} iconPosition="start" />
            <Tab label="Missing COGS" icon={<WarningIcon color="error" />} iconPosition="start" />
            <Tab label="BOMs" icon={<BundleIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <CardContent>
          {/* SKU Mappings Tab (Tab 4) */}
          {activeTab === 4 && (
            <UnmappedSkuMappings onDataChange={loadData} />
          )}

          {/* ASIN Mappings Tab (Tab 5) */}
          {activeTab === 5 && (
            <AsinMapping embedded />
          )}

          {/* Batch Reconciliation Tab (Tab 6) */}
          {activeTab === 6 && (
            <ReconciliationUnmappedSkus />
          )}

          {/* Missing COGS Tab (Tab 7) */}
          {activeTab === 7 && (
            <MissingCOGS
              brandId={selectedBrand?.id}
              onCogsAdded={loadData}
            />
          )}

          {/* BOMs Tab (Tab 8) — the BOM catalog, embedded. Rows open the
              product page's BOM tab where recipes/kits are edited. */}
          {activeTab === 8 && (
            <BomCatalogPanel embedded />
          )}

          {/* Original tabs content (0-3) */}
          {activeTab <= 3 && (
            <>
              {/* Search + status + category + stock filters */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search by SKU or product name..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                  size="small"
                  sx={{ width: 300 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active only</MenuItem>
                    <MenuItem value="inactive">Inactive only</MenuItem>
                  </Select>
                </FormControl>
                {activeTab !== 3 && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={categoryFilter}
                        label="Category"
                        onChange={(e) => { setCategoryFilter(e.target.value); setSubCategoryFilter(''); setPage(0); }}
                      >
                        <MenuItem value=""><em>All categories</em></MenuItem>
                        {categoryTree.map((c) => (
                          <MenuItem key={c.category} value={c.category}>{c.category}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }} disabled={!categoryFilter}>
                      <InputLabel>Sub-category</InputLabel>
                      <Select
                        value={subCategoryFilter}
                        label="Sub-category"
                        onChange={(e) => { setSubCategoryFilter(e.target.value); setPage(0); }}
                      >
                        <MenuItem value=""><em>All sub-categories</em></MenuItem>
                        {filterSubOptions.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="When on, hides SKUs with no stock in any warehouse, no incoming transfers, and no open purchase orders.">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={hideUnstocked}
                            onChange={(e) => { setHideUnstocked(e.target.checked); setPage(0); }}
                          />
                        }
                        label="Hide unstocked (no stock / incoming / POs)"
                      />
                    </Tooltip>
                  </>
                )}
              </Box>

          {/* List Table (Shared for all tabs) */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {activeTab === 3 ? (
                    <>
                      <TableCell>Alias SKU</TableCell>
                      <TableCell>Primary SKU</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'sku'}
                          direction={orderBy === 'sku' ? order : 'asc'}
                          onClick={() => handleRequestSort('sku')}
                        >
                          SKU
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'product_name'}
                          direction={orderBy === 'product_name' ? order : 'asc'}
                          onClick={() => handleRequestSort('product_name')}
                        >
                          Product Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'category'}
                          direction={orderBy === 'category' ? order : 'asc'}
                          onClick={() => handleRequestSort('category')}
                        >
                          Category
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'gtin'}
                          direction={orderBy === 'gtin' ? order : 'asc'}
                          onClick={() => handleRequestSort('gtin')}
                        >
                          GTIN
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'asin'}
                          direction={orderBy === 'asin' ? order : 'asc'}
                          onClick={() => handleRequestSort('asin')}
                        >
                          ASIN
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'unit_cost'}
                          direction={orderBy === 'unit_cost' ? order : 'asc'}
                          onClick={() => handleRequestSort('unit_cost')}
                        >
                          Unit Cost
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'units_sold_90d'}
                          direction={orderBy === 'units_sold_90d' ? order : 'asc'}
                          onClick={() => handleRequestSort('units_sold_90d')}
                        >
                          Units Sold (90d)
                        </TableSortLabel>
                      </TableCell>
                      {/* Supplier is meaningless for bundles (assembled, not purchased) — hide on Bundles tab */}
                      {activeTab !== 1 && (
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'supplier'}
                            direction={orderBy === 'supplier' ? order : 'asc'}
                            onClick={() => handleRequestSort('supplier')}
                          >
                            Supplier
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'lead_time_days'}
                          direction={orderBy === 'lead_time_days' ? order : 'asc'}
                          onClick={() => handleRequestSort('lead_time_days')}
                        >
                          Lead Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'reorder_point'}
                          direction={orderBy === 'reorder_point' ? order : 'asc'}
                          onClick={() => handleRequestSort('reorder_point')}
                        >
                          Reorder Point
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {activeTab === 3 ? (
                  // Aliases List
                  aliases
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((alias) => (
                      <TableRow key={alias.sku} hover>
                        <TableCell>{alias.sku}</TableCell>
                        <TableCell>{alias.primarysku}</TableCell>
                        <TableCell>{alias.brand || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenAliasDialog(alias)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteAlias(alias.sku)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  // Products/Bundles/Parts List
                  currentList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => (
                  <TableRow key={product.sku} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Product thumbnail from Shopify */}
                        {product.image_url ? (
                          <Box
                            component="img"
                            src={product.image_url}
                            alt={product.product_name || product.sku}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                              flexShrink: 0,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              bgcolor: 'grey.100',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            <CategoryIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                          </Box>
                        )}
                        <Box>
                          <Typography fontWeight="medium">{product.sku}</Typography>
                          {product.bom_level > 0 && (
                            <Chip
                              label={product.bom_level === 2 ? 'Bundle' : 'Product'}
                              size="small"
                              color={product.bom_level === 2 ? 'secondary' : 'primary'}
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.product_name ? (
                        <Box
                          component="a"
                          title="Open product"
                          onClick={(e) => { e.stopPropagation(); openProductPage(product); }}
                          sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {product.product_name}
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{product.category || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {product.gtin || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {product.asin || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box>
                        <Typography variant="body2">
                          {formatPriceRange(product)}
                        </Typography>
                        {stockSignals[product.sku] !== undefined && (() => {
                          const sig = stockSignals[product.sku];
                          return (
                            <Tooltip
                              arrow
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  {sig.warehouses && sig.warehouses.length > 0 ? (
                                    sig.warehouses.map(([code, qty]) => (
                                      <Box key={code} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                        <span>{code}</span>
                                        <span>{formatNumber(qty)}</span>
                                      </Box>
                                    ))
                                  ) : (
                                    <Box>Not stocked in any warehouse</Box>
                                  )}
                                  {sig.incoming_qty > 0 && (
                                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                      <span>In transit</span><span>{formatNumber(sig.incoming_qty)}</span>
                                    </Box>
                                  )}
                                  {sig.open_po_qty > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                      <span>On open PO</span><span>{formatNumber(sig.open_po_qty)}</span>
                                    </Box>
                                  )}
                                </Box>
                              }
                            >
                              <Typography
                                variant="caption"
                                color={sig.warehouse_count > 0 ? 'text.secondary' : 'warning.main'}
                                sx={{ display: 'block', width: 'fit-content', ml: 'auto', cursor: 'help', textDecoration: 'underline dotted' }}
                              >
                                {sig.warehouse_count} warehouse{sig.warehouse_count !== 1 ? 's' : ''}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                        {(() => {
                          // PO-derived computed cost vs stored COGS. cost_source '' = no computed cost.
                          const computed = parseFloat(product.computed_unit_cost_usd) || 0;
                          const source = product.computed_cost_source || '';
                          if (!source || computed <= 0) return null;
                          const stored = parseFloat(product.calculated_cogs || product.cogs || product.unit_cost || product.min_cost || 0);
                          const diff = Math.abs(computed - stored);
                          const diverges = stored > 0 && diff > 0.10 && (diff / stored) > 0.10;
                          const label = source === 'po_direct' ? 'latest PO' : 'BOM rollup';
                          return (
                            <Tooltip arrow title={`PO-derived cost (${label}): ${formatCurrency(computed)}.${diverges ? ' Differs >10% from stored COGS — review.' : ' In line with stored COGS.'}`}>
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, mt: 0.25, cursor: 'help' }}>
                                <Typography variant="caption" sx={{ color: diverges ? 'warning.main' : 'text.disabled', fontSize: '0.65rem', fontWeight: diverges ? 600 : 400 }}>
                                  calc {formatCurrency(computed)}
                                </Typography>
                                {diverges && <WarningIcon sx={{ fontSize: '0.8rem', color: 'warning.main' }} />}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight={sales90d[product.sku] > 0 ? 600 : 400}
                        color={sales90d[product.sku] > 0 ? 'primary' : 'text.secondary'}
                      >
                        {sales90d[product.sku] != null ? formatNumber(sales90d[product.sku]) : '—'}
                      </Typography>
                    </TableCell>
                    {activeTab !== 1 && (
                      /* Supplier: warehouse-signal supplier first (if the stock endpoint has one),
                         then the product's most-recent-external-PO supplier (Diff bug #9),
                         else the Vendor typed on the product (Rishabh 2026-10-23: parts with no
                         outside PO get their supplier entered by hand). */
                      <TableCell>{(stockSignals[product.sku] && stockSignals[product.sku].supplier) || product.supplier_name || product.vendor || '—'}</TableCell>
                    )}
                    <TableCell align="right">
                      <LeadTimeCell
                        brandId={selectedBrand?.id}
                        product={product}
                        onEdit={(regime, computed, hasOverride) =>
                          setLtOverrideDialog({ open: true, sku: product.sku, regime, computed, hasOverride })
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const rp = reorderPoint(product);
                        if (rp == null) return '—';
                        const lead = productionLeadDays(product);
                        const perDay = (sales90d[product.sku] || 0) / 90;
                        return (
                          <Tooltip
                            arrow
                            title={`Reorder point = production lead time (${lead}d) × daily sales (${perDay.toFixed(1)}/day over 90d). Reorder when on-hand reaches this level.`}
                          >
                            <Typography variant="body2" sx={{ cursor: 'help', display: 'inline-block' }}>
                              {formatNumber(rp)}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(product.is_active === 0 || product.is_active === '0' || product.is_active === false) ? 'Inactive' : 'Active'}
                        size="small"
                        color={(product.is_active === 0 || product.is_active === '0' || product.is_active === false) ? 'default' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => {
                        if (activeTab === 1) {
                          // For bundles tab, find the bundle data with components
                          const bundleData = bundles.find(b => b.bundle_sku === product.sku);
                          if (bundleData) {
                            handleOpenBundleDialog(bundleData);
                          } else {
                            // Fallback: create bundle data from product
                            handleOpenBundleDialog({
                              bundle_sku: product.sku,
                              bundle_name: product.product_name,
                              is_prepacked: product.is_prepacked,
                              is_active: product.is_active,
                              bundle_overhead: product.bundle_overhead,
                              components: []
                            });
                          }
                        } else {
                          openProductPage(product);
                        }
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteProduct(product.sku)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {currentList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <Typography color="textSecondary" sx={{ py: 3 }}>
                        No items found in this category
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={currentList.length}
            page={page}
            onPageChange={(e, newPage) => { setPage(newPage); updateUrlParams({ page: newPage }); }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const newRows = parseInt(e.target.value, 10);
              setRowsPerPage(newRows);
              setPage(0);
              updateUrlParams({ rows: newRows, page: 0 });
            }}
          />
            </>
          )}
        </CardContent>
      </Card>

      </>)}

      {/* Product view/edit — FULL PAGE (query-param routed via ?product=…),
          replaces the old modal Dialog. Rishabh 2026-07-02: a real page with its
          own URL + working Back, not an overlay. Fields below are unchanged. */}
      {productParam && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={closeProductPage} aria-label="Back to products"><ArrowBackIcon /></IconButton>
            {editingProduct && (editingProduct.image_url || editingProduct.thumbnail_url) ? (
              <Box
                component="img"
                src={editingProduct.thumbnail_url || editingProduct.image_url}
                alt=""
                sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              />
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h5" fontWeight={600} noWrap>
                {editingProduct ? (productForm.product_name || editingProduct.sku) : 'Add Product'}
              </Typography>
              {editingProduct && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {editingProduct.sku}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={productForm.is_active ? 'success' : 'default'}
                    label={productForm.is_active ? 'Active' : 'Inactive'}
                  />
                  {productForm.is_bundle ? <Chip size="small" variant="outlined" label="Bundle / Kit" /> : null}
                  {selectedBrand?.name ? <Chip size="small" variant="outlined" label={selectedBrand.name} /> : null}
                  {/* Where-used: component → parent SKU navigation (Rishabh
                      2026-07-03). Compact by design — his correction after v1:
                      a product inside ~40 kits flooded the header with chips.
                      1–3 parents render inline; more collapse to a single
                      "Used in N products" chip that opens a clickable list. */}
                  {(productDetail?.component_usage?.finished_goods?.length > 0) && (() => {
                    const fgs = productDetail.component_usage.finished_goods;
                    if (fgs.length <= 3) {
                      return (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            · Used in:
                          </Typography>
                          {fgs.map((fg) => (
                            <Tooltip key={fg} title="Open this product">
                              <Chip
                                size="small"
                                clickable
                                color="primary"
                                variant="outlined"
                                label={fg}
                                onClick={() => jumpToProduct(fg)}
                                sx={{ fontFamily: 'monospace' }}
                              />
                            </Tooltip>
                          ))}
                        </>
                      );
                    }
                    return (
                      <>
                        <Chip
                          size="small"
                          clickable
                          color="primary"
                          variant="outlined"
                          label={`Used in ${fgs.length} products`}
                          onClick={(e) => setUsedInAnchor(e.currentTarget)}
                        />
                        <Menu
                          anchorEl={usedInAnchor}
                          open={Boolean(usedInAnchor)}
                          onClose={() => setUsedInAnchor(null)}
                          PaperProps={{ sx: { maxHeight: 360 } }}
                        >
                          {fgs.map((fg) => (
                            <MenuItem
                              key={fg}
                              dense
                              onClick={() => { setUsedInAnchor(null); jumpToProduct(fg); }}
                            >
                              <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{fg}</Typography>
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
            {/* Add mode: the product lands under the SELECTED brand — make that
                explicit and changeable right here (Rishabh 2026-07-03: "how are
                you verifying for which brand that product is?"). */}
            {!editingProduct && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Brand *</InputLabel>
                <Select
                  value={selectedBrand?.id || ''}
                  label="Brand *"
                  onChange={handleBrandChange}
                >
                  {excludeCompanyLevel(brands).map((b) => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
          {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>{warning}</Alert>}
          {productRefreshError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setProductRefreshError(null)}>{productRefreshError}</Alert>}
          {/* Guardrail: inactive product still consumed by active recipes —
              deactivation alone does NOT stop manufacturing use. */}
          {editingProduct && !productForm.is_active && (productDetail?.component_usage?.lines > 0) && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={(
                <Button color="inherit" size="small" onClick={() => { setError(null); setEndBomOpen(true); }} disabled={endingBom}>
                  End use in all BOMs
                </Button>
              )}
            >
              This product is <b>Inactive</b> but is still a component in{' '}
              <b>{productDetail.component_usage.lines} active BOM line{productDetail.component_usage.lines > 1 ? 's' : ''}</b>{' '}
              ({productDetail.component_usage.finished_goods.length} finished good{productDetail.component_usage.finished_goods.length > 1 ? 's' : ''}) —
              future builds will still consume it until its BOM lines are ended.
            </Alert>
          )}

          {/* Bulk end-date confirm */}
          <Dialog open={endBomOpen} onClose={() => !endingBom && setEndBomOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>End this component's use in all BOMs?</DialogTitle>
            <DialogContent>
              {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
              {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                Ends <b>{editingProduct?.sku}</b> in{' '}
                <b>{productDetail?.component_usage?.lines || 0} BOM line{(productDetail?.component_usage?.lines || 0) > 1 ? 's' : ''}</b>, effective <b>immediately</b>.
                The lines are end-dated, not deleted — historical builds and past cost calculations are unaffected.
                From this moment, these recipes build without it:
              </Typography>
              <Box sx={{ maxHeight: 180, overflowY: 'auto', bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                {(productDetail?.component_usage?.finished_goods || []).map((fg) => (
                  <Typography key={fg} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>{fg}</Typography>
                ))}
              </Box>
              <Alert severity="info" sx={{ mt: 1.5 }}>
                If any of these finished goods still ships with this component, use the BOM editor's{' '}
                <b>Replace</b> flow instead — plain removal would make its stock deductions wrong.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEndBomOpen(false)} disabled={endingBom}>Cancel</Button>
              <Button variant="contained" color="warning" onClick={handleEndBomUse} disabled={endingBom}>
                {endingBom ? 'Ending…' : `End use in ${productDetail?.component_usage?.lines || 0} line${(productDetail?.component_usage?.lines || 0) === 1 ? '' : 's'}`}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Costs/Pricing/BOM only exist for a saved product — Add mode stays a plain form */}
          {editingProduct && (
            <Tabs
              value={productPageTab}
              onChange={(e, v) => setProductPageTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              <Tab label="Details" />
              <Tab label="Costs" />
              <Tab label="Pricing" />
              <Tab label="BOM" />
            </Tabs>
          )}
          {editingProduct && productPageTab === 1 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
              <ProductCostsTab detail={productDetail} loading={productDetailLoading} />
            </Box>
          )}
          {editingProduct && productPageTab === 2 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
              <ProductPricingTab
                detail={productDetail}
                loading={productDetailLoading}
                onSaveRrp={handleSaveRrp}
                savingRrp={savingRrp}
              />
            </Box>
          )}
          {editingProduct && productPageTab === 3 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
              <ProductBomTab
                detail={productDetail}
                loading={productDetailLoading}
                onEditProduction={handleEditProductionBom}
                onEditKit={handleEditKit}
              />
            </Box>
          )}
          <Box sx={{ display: (!editingProduct || productPageTab === 0) ? 'block' : 'none' }}>
          <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="SKU"
              value={productForm.sku}
              onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
              required
              disabled={!!editingProduct}
              fullWidth
            />
            <TextField
              label="Product Name"
              value={productForm.product_name}
              onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })}
              fullWidth
            />
            {/* Product type — creation must be explicit about the level (Rishabh
                2026-07-03: "how will it identify Component vs L1 vs Bundle?").
                Bundles are still the Kit/Bundle switch below (saved as L2).
                Shown on EDIT too so a wrong creation choice is fixable
                (Rishabh 2026-07-06: a bottle component stuck under Products). */}
            {!productForm.is_bundle && (
              <FormControl fullWidth>
                <InputLabel>Product type</InputLabel>
                <Select
                  value={productForm.bom_level ?? 1}
                  label="Product type"
                  onChange={(e) => setProductForm({ ...productForm, bom_level: e.target.value })}
                >
                  <MenuItem value={1}>Sellable product (L1) — sold on its own</MenuItem>
                  <MenuItem value={0}>Component / packaging (L0) — used inside recipes, not sold</MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5, mt: 0.5 }}>
                  {editingProduct
                    ? 'Changing this moves the product between the Products and Parts tabs.'
                    : 'Decides which tab it appears under (Products vs Parts) and whether it can be used as a BOM component.'}
                </Typography>
              </FormControl>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={productForm.category || ''}
                    label="Category"
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value, sub_category: '' })}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {dialogCategoryOptions.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth disabled={!productForm.category}>
                  <InputLabel>Sub-category</InputLabel>
                  <Select
                    value={productForm.sub_category || ''}
                    label="Sub-category"
                    onChange={(e) => setProductForm({ ...productForm, sub_category: e.target.value })}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {dialogSubCategoryOptions.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Unit Cost"
                  type="number"
                  value={productForm.unit_cost}
                  onChange={(e) => setProductForm({ ...productForm, unit_cost: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={productForm.currency}
                    label="Currency"
                    onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
                  >
                    {POPULAR_CURRENCIES.map((c) => (
                      <MenuItem key={c.code} value={c.code}>{c.code} · {c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Supplier"
              value={productForm.supplier}
              onChange={(e) => setProductForm({ ...productForm, supplier: e.target.value })}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Lead Time (days)"
                  type="number"
                  value={productForm.lead_time_days}
                  onChange={(e) => setProductForm({ ...productForm, lead_time_days: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Reorder Point"
                  type="number"
                  value={productForm.reorder_point}
                  onChange={(e) => setProductForm({ ...productForm, reorder_point: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Live engine-computed availability (stock-aware, parallel
                components + sequential production) — existing products only. */}
            {editingProduct && selectedBrand?.id && (
              <LeadTimeAvailabilityPanel brandId={selectedBrand.id} sku={editingProduct.sku} />
            )}

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="GTIN"
                  value={productForm.gtin}
                  onChange={(e) => setProductForm({ ...productForm, gtin: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="ASIN"
                  value={productForm.asin}
                  onChange={(e) => setProductForm({ ...productForm, asin: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={3}>
                <TextField
                  label="Weight"
                  type="number"
                  value={productForm.weight}
                  onChange={(e) => setProductForm({ ...productForm, weight: e.target.value })}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  select
                  label="Unit"
                  value={productForm.weight_unit || 'kg'}
                  onChange={(e) => setProductForm({ ...productForm, weight_unit: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="kg">kg</MenuItem>
                  <MenuItem value="g">g</MenuItem>
                  <MenuItem value="lb">lb</MenuItem>
                  <MenuItem value="oz">oz</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="HS Code"
                  value={productForm.hs_code}
                  onChange={(e) => setProductForm({ ...productForm, hs_code: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Collapsible Additional Details */}
            <Button
              size="small"
              onClick={() => setShowProductDetails(!showProductDetails)}
              endIcon={<ExpandMoreIcon sx={{ transform: showProductDetails ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {showProductDetails ? 'Hide' : 'Show'} Additional Details
            </Button>
            <Collapse in={showProductDetails}>
              <Stack spacing={2}>
                {/* Dimensions */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Dimensions</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <TextField label="Length" type="number" value={productForm.length_cm}
                      onChange={(e) => setProductForm({ ...productForm, length_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Width" type="number" value={productForm.width_cm}
                      onChange={(e) => setProductForm({ ...productForm, width_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Height" type="number" value={productForm.height_cm}
                      onChange={(e) => setProductForm({ ...productForm, height_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField select label="Unit" value={productForm.dimension_unit || 'cm'}
                      onChange={(e) => setProductForm({ ...productForm, dimension_unit: e.target.value })}
                      fullWidth size="small">
                      <MenuItem value="cm">cm</MenuItem>
                      <MenuItem value="in">in</MenuItem>
                      <MenuItem value="mm">mm</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* Compliance */}
                <Typography variant="caption" color="text.secondary">Compliance</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="Country of Origin" value={productForm.country_of_origin}
                      onChange={(e) => setProductForm({ ...productForm, country_of_origin: e.target.value })}
                      fullWidth size="small" placeholder="e.g. CN, AU, US" />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      control={<Switch checked={productForm.is_hazmat}
                        onChange={(e) => setProductForm({ ...productForm, is_hazmat: e.target.checked, hazmat_class: e.target.checked ? productForm.hazmat_class : '' })} size="small" />}
                      label="Hazmat"
                    />
                  </Grid>
                  {productForm.is_hazmat && (
                    <Grid item xs={5}>
                      <TextField label="Hazmat Class" value={productForm.hazmat_class}
                        onChange={(e) => setProductForm({ ...productForm, hazmat_class: e.target.value })}
                        fullWidth size="small" placeholder="e.g. Class 3, Class 9" />
                    </Grid>
                  )}
                </Grid>

                {/* Procurement */}
                <Typography variant="caption" color="text.secondary">Procurement</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="Vendor" value={productForm.vendor}
                      onChange={(e) => setProductForm({ ...productForm, vendor: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Manufacturer" value={productForm.manufacturer}
                      onChange={(e) => setProductForm({ ...productForm, manufacturer: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Manufacturer Part #" value={productForm.manufacturer_part_number}
                      onChange={(e) => setProductForm({ ...productForm, manufacturer_part_number: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="MOQ" type="number" value={productForm.moq}
                      onChange={(e) => setProductForm({ ...productForm, moq: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Case Pack" type="number" value={productForm.case_pack}
                      onChange={(e) => setProductForm({ ...productForm, case_pack: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Stock Unit" select fullWidth size="small"
                      value={productForm.stock_uom || 'units'}
                      onChange={(e) => setProductForm({ ...productForm, stock_uom: e.target.value })}
                      helperText="Unit this SKU's stock is counted in (bulk items: ml/g/kg). Set BEFORE any stock activity — changing it later reinterprets existing quantities without converting them."
                    >
                      {['units', 'ml', 'l', 'mg', 'g', 'kg', 'mm', 'cm', 'm', 'yards'].map((u) => (
                        <MenuItem key={u} value={u}>{u}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </Collapse>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Warehouse Shipping Cost"
                  type="number"
                  value={productForm.warehouse_shipping_cost}
                  onChange={(e) => setProductForm({ ...productForm, warehouse_shipping_cost: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Inbound Freight Cost"
                  type="number"
                  value={productForm.inbound_freight_cost}
                  onChange={(e) => setProductForm({ ...productForm, inbound_freight_cost: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <TextField
              label="Notes"
              value={productForm.notes}
              onChange={(e) => setProductForm({ ...productForm, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            
            {/* Warehouse-Specific COGS */}
            {!editingProduct && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={productForm.apply_to_all_warehouses}
                      onChange={(e) => setProductForm({ ...productForm, apply_to_all_warehouses: e.target.checked })}
                    />
                  }
                  label="Apply same COGS to all warehouses"
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {productForm.apply_to_all_warehouses 
                    ? "The unit cost above will be used for all warehouses"
                    : "You can set different COGS per warehouse after creating the product"}
                </Typography>
              </Box>
            )}
            
            {editingProduct && productForm.warehouse_specific_costs.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Warehouse-Specific COGS ({productForm.warehouse_specific_costs.length} warehouses)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  This product has different costs per warehouse. Edit each below:
                </Typography>
                {productForm.warehouse_specific_costs.map((whCost, index) => (
                  <Box key={index} sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <Typography variant="body2" fontWeight="medium">
                          {whCost.warehouse_code}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label="Unit Cost"
                          type="number"
                          size="small"
                          value={whCost.cogs}
                          onChange={(e) => {
                            const updated = [...productForm.warehouse_specific_costs];
                            updated[index] = { ...updated[index], cogs: e.target.value };
                            setProductForm({ ...productForm, warehouse_specific_costs: updated });
                          }}
                          fullWidth
                        />
                      </Grid>
                      {/* Per-warehouse source currency (Rishabh 2026-07-03) —
                          e.g. an AU manufacturer's cost entered in AUD while
                          US warehouses stay USD. */}
                      <Grid item xs={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Currency</InputLabel>
                          <Select
                            value={whCost.currency || productForm.currency || 'USD'}
                            label="Currency"
                            onChange={(e) => {
                              const updated = [...productForm.warehouse_specific_costs];
                              updated[index] = { ...updated[index], currency: e.target.value };
                              setProductForm({ ...productForm, warehouse_specific_costs: updated });
                            }}
                          >
                            {POPULAR_CURRENCIES.map((c) => (
                              <MenuItem key={c.code} value={c.code}>{c.code}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Supplier (optional)"
                          size="small"
                          value={whCost.supplier || ''}
                          onChange={(e) => {
                            const updated = [...productForm.warehouse_specific_costs];
                            updated[index] = { ...updated[index], supplier: e.target.value };
                            setProductForm({ ...productForm, warehouse_specific_costs: updated });
                          }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={productForm.is_active}
                  onChange={(e) => setProductForm({ ...productForm, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            {/* Deactivating does NOT touch BOMs — surface that at the moment of the flip */}
            {!productForm.is_active && (productDetail?.component_usage?.lines > 0) && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: -1 }}>
                Still a component in {productDetail.component_usage.lines} active BOM line{productDetail.component_usage.lines > 1 ? 's' : ''} —
                deactivating does not remove it from recipes. Use "End use in all BOMs" (banner above) to stop future builds consuming it.
              </Typography>
            )}

            {/* Kit/Bundle Configuration — not applicable to L0 components */}
            {(productForm.bom_level ?? 1) !== 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Kit/Bundle Configuration
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={productForm.is_bundle}
                    onChange={(e) => setProductForm({
                      ...productForm,
                      is_bundle: e.target.checked,
                      bom_level: e.target.checked ? 2 : 1,
                      is_prepacked: e.target.checked ? productForm.is_prepacked : false,
                      components: e.target.checked ? (productForm.components || [{ component_sku: '', quantity: '' }]) : []
                    })}
                  />
                }
                label="This is a bundle/kit (has component SKUs)"
              />
              {productForm.is_bundle && (
                <Box sx={{ ml: 3, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={productForm.is_prepacked}
                        onChange={(e) => setProductForm({ ...productForm, is_prepacked: e.target.checked })}
                      />
                    }
                    label="Pre-packed kit (stored as assembled unit)"
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    {productForm.is_prepacked
                      ? "Pre-packed: Kit is stored as a complete unit. Inventory tracked at kit level."
                      : "On-demand: Components assembled at fulfillment time. Inventory tracked at component level."}
                  </Typography>

                  {/* Inline Component Selector */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Components</Typography>
                    {(productForm.components || []).map((comp, index) => {
                      const componentProduct = groupedProducts.find(p => p.sku === comp.component_sku);
                      return (
                        <Grid container spacing={1} key={index} alignItems="center" sx={{ mb: 1 }}>
                          <Grid item xs="auto">
                            {componentProduct?.image_url ? (
                              <Box
                                component="img"
                                src={componentProduct.image_url}
                                alt=""
                                sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <Box sx={{ width: 32, height: 32, borderRadius: 0.5, bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider' }}>
                                <CategoryIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                              </Box>
                            )}
                          </Grid>
                          <Grid item xs>
                            <Autocomplete
                              size="small"
                              options={groupedProducts.filter(p => p.sku !== productForm.sku && p.bom_level < 2)}
                              getOptionLabel={(option) => typeof option === 'string' ? option : `${option.sku} - ${option.product_name || '(No name)'}`}
                              value={groupedProducts.find(p => p.sku === comp.component_sku) || null}
                              onChange={(e, newValue) => {
                                const updated = [...productForm.components];
                                updated[index] = { ...updated[index], component_sku: newValue?.sku || '' };
                                setProductForm({ ...productForm, components: updated });
                              }}
                              isOptionEqualToValue={(option, value) => option.sku === value.sku}
                              renderOption={(props, option) => (
                                <li {...props} key={option.sku}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {option.image_url && (
                                      <Box component="img" src={option.image_url} alt="" sx={{ width: 20, height: 20, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                                    )}
                                    {option.sku} - {option.product_name || '(No name)'}
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => <TextField {...params} label="Component SKU" />}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              label="Qty"
                              type="number"
                              value={comp.quantity}
                              onChange={(e) => {
                                const updated = [...productForm.components];
                                updated[index] = { ...updated[index], quantity: e.target.value };
                                setProductForm({ ...productForm, components: updated });
                              }}
                              size="small"
                              fullWidth
                              inputProps={{ min: 1 }}
                            />
                          </Grid>
                          <Grid item xs="auto">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const updated = productForm.components.filter((_, i) => i !== index);
                                setProductForm({ ...productForm, components: updated.length > 0 ? updated : [{ component_sku: '', quantity: '' }] });
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      );
                    })}
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setProductForm({
                        ...productForm,
                        components: [...(productForm.components || []), { component_sku: '', quantity: '' }]
                      })}
                      sx={{ mt: 0.5 }}
                    >
                      Add Component
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                      Select components that make up this bundle. COGS will be auto-calculated from component costs. For geography-specific components, use the Bundles tab after saving.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            )}
          </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3, maxWidth: 900, mx: 'auto', width: '100%' }}>
            <Button onClick={closeProductPage}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveProduct}
              disabled={!productForm.sku}
            >
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </Box>
          </Box>
        </Box>
      )}

      {/* Bundle Dialog with Geography Tabs */}
      <Dialog open={bundleDialogOpen} onClose={() => setBundleDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editingBundle ? 'Edit Bundle' : 'Create Bundle'}</DialogTitle>
        <DialogContent>
          {/* Save / status errors (e.g. the 409 "is an alias of" refusal) — the page-level Alert sits behind this modal. */}
          {error && <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Bundle SKU"
                  value={bundleForm.bundle_sku}
                  onChange={(e) => setBundleForm({ ...bundleForm, bundle_sku: e.target.value })}
                  required
                  disabled={!!editingBundle}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Bundle Name"
                  value={bundleForm.bundle_name}
                  onChange={(e) => setBundleForm({ ...bundleForm, bundle_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="GTIN / UPC / EAN"
                  value={bundleForm.gtin}
                  onChange={(e) => setBundleForm({ ...bundleForm, gtin: e.target.value })}
                  fullWidth
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Bundle Overhead Cost"
                  type="number"
                  value={bundleForm.bundle_overhead}
                  onChange={(e) => setBundleForm({ ...bundleForm, bundle_overhead: e.target.value })}
                  fullWidth
                  helperText="Additional cost for packaging/assembly"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label="Weight"
                  type="number"
                  value={bundleForm.weight}
                  onChange={(e) => setBundleForm({ ...bundleForm, weight: e.target.value })}
                  fullWidth
                  placeholder="Optional"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={1}>
                <TextField
                  select
                  label="Unit"
                  value={bundleForm.weight_unit || 'kg'}
                  onChange={(e) => setBundleForm({ ...bundleForm, weight_unit: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="kg">kg</MenuItem>
                  <MenuItem value="g">g</MenuItem>
                  <MenuItem value="lb">lb</MenuItem>
                  <MenuItem value="oz">oz</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="HS Code"
                  value={bundleForm.hs_code}
                  onChange={(e) => setBundleForm({ ...bundleForm, hs_code: e.target.value })}
                  fullWidth
                  placeholder="Optional"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Lead Time (days)"
                  type="number"
                  value={bundleForm.lead_time_days}
                  onChange={(e) => setBundleForm({ ...bundleForm, lead_time_days: e.target.value })}
                  fullWidth
                  placeholder="Optional"
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>

            {/* Collapsible Additional Details */}
            <Button
              size="small"
              onClick={() => setShowBundleDetails(!showBundleDetails)}
              endIcon={<ExpandMoreIcon sx={{ transform: showBundleDetails ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {showBundleDetails ? 'Hide' : 'Show'} Additional Details
            </Button>
            <Collapse in={showBundleDetails}>
              <Stack spacing={2}>
                {/* Dimensions */}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Dimensions</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <TextField label="Length" type="number" value={bundleForm.length_cm}
                      onChange={(e) => setBundleForm({ ...bundleForm, length_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Width" type="number" value={bundleForm.width_cm}
                      onChange={(e) => setBundleForm({ ...bundleForm, width_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Height" type="number" value={bundleForm.height_cm}
                      onChange={(e) => setBundleForm({ ...bundleForm, height_cm: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 0, step: 0.1 }} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField select label="Unit" value={bundleForm.dimension_unit || 'cm'}
                      onChange={(e) => setBundleForm({ ...bundleForm, dimension_unit: e.target.value })}
                      fullWidth size="small">
                      <MenuItem value="cm">cm</MenuItem>
                      <MenuItem value="in">in</MenuItem>
                      <MenuItem value="mm">mm</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                {/* Compliance */}
                <Typography variant="caption" color="text.secondary">Compliance</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="Country of Origin" value={bundleForm.country_of_origin}
                      onChange={(e) => setBundleForm({ ...bundleForm, country_of_origin: e.target.value })}
                      fullWidth size="small" placeholder="e.g. CN, AU, US" />
                  </Grid>
                  <Grid item xs={3}>
                    <FormControlLabel
                      control={<Switch checked={bundleForm.is_hazmat}
                        onChange={(e) => setBundleForm({ ...bundleForm, is_hazmat: e.target.checked, hazmat_class: e.target.checked ? bundleForm.hazmat_class : '' })} size="small" />}
                      label="Hazmat"
                    />
                  </Grid>
                  {bundleForm.is_hazmat && (
                    <Grid item xs={5}>
                      <TextField label="Hazmat Class" value={bundleForm.hazmat_class}
                        onChange={(e) => setBundleForm({ ...bundleForm, hazmat_class: e.target.value })}
                        fullWidth size="small" placeholder="e.g. Class 3, Class 9" />
                    </Grid>
                  )}
                </Grid>

                {/* Procurement */}
                <Typography variant="caption" color="text.secondary">Procurement</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="Vendor" value={bundleForm.vendor}
                      onChange={(e) => setBundleForm({ ...bundleForm, vendor: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Manufacturer" value={bundleForm.manufacturer}
                      onChange={(e) => setBundleForm({ ...bundleForm, manufacturer: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Manufacturer Part #" value={bundleForm.manufacturer_part_number}
                      onChange={(e) => setBundleForm({ ...bundleForm, manufacturer_part_number: e.target.value })}
                      fullWidth size="small" />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField label="MOQ" type="number" value={bundleForm.moq}
                      onChange={(e) => setBundleForm({ ...bundleForm, moq: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 1 }} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Case Pack" type="number" value={bundleForm.case_pack}
                      onChange={(e) => setBundleForm({ ...bundleForm, case_pack: e.target.value })}
                      fullWidth size="small" inputProps={{ min: 1 }} />
                  </Grid>
                </Grid>
              </Stack>
            </Collapse>

            {/* Pre-packed Option */}
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bundleForm.is_prepacked}
                    onChange={(e) => setBundleForm({ ...bundleForm, is_prepacked: e.target.checked })}
                  />
                }
                label="Pre-packed kit (stored as assembled unit)"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                {bundleForm.is_prepacked
                  ? "Pre-packed: Kit is stored as a complete unit. Inventory tracked at kit level."
                  : "On-demand: Components assembled at fulfillment time. Inventory tracked at component level."}
              </Typography>
            </Box>

            {/* Active / Inactive toggle — saves immediately via a dedicated status
                endpoint so it's never blocked by component (BOM) validation. */}
            <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={bundleForm.is_active}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setBundleForm((f) => ({ ...f, is_active: next }));
                      if (editingBundle && bundleForm.bundle_sku) {
                        try {
                          await setProductActiveStatus(selectedBrand.id, bundleForm.bundle_sku, next);
                          setSuccess(`Bundle marked ${next ? 'Active' : 'Inactive'}`);
                          // Update the lists IN PLACE — a full loadData() here
                          // blanked the table behind the dialog and read as
                          // "the page refreshes" (Rishabh 2026-07-06).
                          const flag = next ? 1 : 0;
                          setProducts((prev) => prev.map((p) => p.sku === bundleForm.bundle_sku ? { ...p, is_active: flag } : p));
                          setBundles((prev) => prev.map((b) => b.bundle_sku === bundleForm.bundle_sku ? { ...b, is_active: flag } : b));
                        } catch (err) {
                          setBundleForm((f) => ({ ...f, is_active: !next }));
                          setError(cogsWriteErrorMessage(err, 'Failed to update bundle status'));
                        }
                      }
                    }}
                  />
                }
                label="Active"
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Toggling this saves immediately (you don't need to click Save). It only
                affects the bundle itself — the individual component SKUs and their stock
                are not changed.
              </Typography>
            </Box>

            {/* Geography Tabs */}
            <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Components by Geography</Typography>
              <Tabs
                value={bundleForm.selectedGeography}
                onChange={(e, newValue) => handleGeographyTabChange(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {GEOGRAPHY_OPTIONS.map(geo => {
                  const hasComponents = bundleForm.componentsByGeography[geo]?.some(c => c.component_sku);
                  return (
                    <Tab
                      key={geo}
                      value={geo}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {geo}
                          {hasComponents && <Chip size="small" label={bundleForm.componentsByGeography[geo]?.length || 0} sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} />}
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            {/* Components for selected geography */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, minHeight: 200 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  {bundleForm.selectedGeography} Components
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Copy to...</InputLabel>
                  <Select
                    label="Copy to..."
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleCopyComponentsToGeography(e.target.value);
                      }
                    }}
                  >
                    {GEOGRAPHY_OPTIONS.filter(g => g !== bundleForm.selectedGeography).map(geo => (
                      <MenuItem key={geo} value={geo}>Copy to {geo}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {(bundleForm.componentsByGeography[bundleForm.selectedGeography] || []).map((comp, index) => {
                const componentProduct = getProductBySku(comp.component_sku);
                return (
                <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 1.5 }}>
                  {/* Thumbnail */}
                  <Grid item xs="auto">
                    {componentProduct?.image_url ? (
                      <Box
                        component="img"
                        src={componentProduct.image_url}
                        alt={componentProduct.product_name || comp.component_sku}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <CategoryIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                      </Box>
                    )}
                  </Grid>
                  {/* SKU Selector */}
                  <Grid item xs>
                    <Autocomplete
                      size="small"
                      options={groupedProducts}
                      getOptionLabel={(option) => typeof option === 'string' ? option : `${option.sku} - ${option.product_name || '(No name)'}`}
                      value={groupedProducts.find(p => p.sku === comp.component_sku) || null}
                      onChange={(e, newValue) => handleUpdateBundleComponent(index, 'component_sku', newValue?.sku || '')}
                      isOptionEqualToValue={(option, value) => option.sku === value.sku}
                      renderOption={(props, option) => (
                        <li {...props} key={option.sku}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {option.image_url && (
                              <Box component="img" src={option.image_url} alt="" sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            {option.sku} - {option.product_name || '(No name)'}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => <TextField {...params} label="Component SKU" />}
                      fullWidth
                    />
                  </Grid>
                  {/* Quantity */}
                  <Grid item xs={2}>
                    <TextField
                      label="Qty"
                      type="number"
                      value={comp.quantity}
                      onChange={(e) => handleUpdateBundleComponent(index, 'quantity', e.target.value)}
                      size="small"
                      fullWidth
                      required
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  {/* Effective dates */}
                  <Grid item xs={3}>
                    {comp.effective_from && (
                      <Typography variant="caption" color="text.secondary">
                        {comp.effective_from?.split(' ')[0]} - {comp.effective_to?.split(' ')[0]?.replace('2099-12-31', 'ongoing')}
                      </Typography>
                    )}
                  </Grid>
                  {/* Replace & Delete buttons */}
                  <Grid item xs="auto">
                    <Tooltip title="Replace this component">
                      <IconButton
                        onClick={() => handleOpenReplaceDialog(comp)}
                        color="primary"
                        size="small"
                        disabled={!comp.component_sku}
                      >
                        <SwapIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      onClick={() => handleRemoveBundleComponent(index)}
                      disabled={(bundleForm.componentsByGeography[bundleForm.selectedGeography] || []).length === 1}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
                );
              })}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddBundleComponent}
                sx={{ mt: 1 }}
              >
                Add Component
              </Button>
            </Box>

            {/* Summary of all geographies */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Configured geographies: {Object.entries(bundleForm.componentsByGeography)
                  .filter(([, comps]) => comps.some(c => c.component_sku))
                  .map(([geo, comps]) => `${geo} (${comps.filter(c => c.component_sku).length})`)
                  .join(', ') || 'None'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBundleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveBundle}
            disabled={!bundleForm.bundle_sku || !Object.values(bundleForm.componentsByGeography).some(comps => comps.some(c => c.component_sku && c.quantity))}
          >
            {editingBundle ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace Component Dialog */}
      <Dialog open={replaceDialogOpen} onClose={() => setReplaceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Replace Component ({bundleForm.selectedGeography})
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Current component being replaced */}
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>Currently ending:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                {(() => {
                  const oldProduct = getProductBySku(replaceForm.replacing_sku);
                  return (
                    <>
                      {oldProduct?.image_url ? (
                        <Box component="img" src={oldProduct.image_url} alt="" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                      ) : (
                        <Box sx={{ width: 40, height: 40, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CategoryIcon sx={{ color: 'grey.400' }} />
                        </Box>
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{replaceForm.replacing_sku}</Typography>
                        <Typography variant="caption" color="text.secondary">{oldProduct?.product_name || '(Unknown product)'}</Typography>
                      </Box>
                      <Chip label={`Qty: ${replaceForm.replacing_quantity}`} size="small" sx={{ ml: 'auto' }} />
                    </>
                  );
                })()}
              </Box>
            </Box>

            <TextField
              label="End Date (component expires)"
              type="date"
              value={replaceForm.end_date}
              onChange={(e) => setReplaceForm({ ...replaceForm, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: '2000-01-01' }}
              fullWidth
              helperText="Replacement starts the day after this date"
            />

            {/* New replacement component */}
            <Box sx={{ bgcolor: 'success.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="caption" color="success.dark" gutterBottom>Replace with (starting {
                (() => {
                  const d = new Date(replaceForm.end_date);
                  d.setDate(d.getDate() + 1);
                  return d.toISOString().split('T')[0];
                })()
              }):</Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={8}>
                  <Autocomplete
                    size="small"
                    options={groupedProducts}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.sku} - ${option.product_name || '(No name)'}`}
                    value={groupedProducts.find(p => p.sku === replaceForm.new_sku) || null}
                    onChange={(e, newValue) => setReplaceForm({ ...replaceForm, new_sku: newValue?.sku || '' })}
                    isOptionEqualToValue={(option, value) => option.sku === value.sku}
                    renderOption={(props, option) => (
                      <li {...props} key={option.sku}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.image_url && (
                            <Box component="img" src={option.image_url} alt="" sx={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 0.5 }} onError={(e) => { e.target.style.display = 'none'; }} />
                          )}
                          {option.sku} - {option.product_name || '(No name)'}
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="New Component SKU" />}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Quantity"
                    type="number"
                    value={replaceForm.new_quantity}
                    onChange={(e) => setReplaceForm({ ...replaceForm, new_quantity: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>

            <Alert severity="info" sx={{ mt: 1 }}>
              The old component will end on <strong>{replaceForm.end_date}</strong> and the new component will start the next day. Other components in this geography are not affected.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReplaceGeography}
            startIcon={<SwapIcon />}
            disabled={!replaceForm.end_date || !replaceForm.new_sku || !replaceForm.new_quantity}
          >
            Replace Components
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alias Dialog */}
      <Dialog open={aliasDialogOpen} onClose={() => setAliasDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAlias ? 'Edit Alias' : 'Create Alias'}</DialogTitle>
        <DialogContent>
          {/* Save errors (e.g. the 403 / 409 alias refusals) — the page-level Alert sits behind this modal (DT-1855). */}
          {error && <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Alias SKU"
              value={aliasForm.sku}
              onChange={(e) => setAliasForm({ ...aliasForm, sku: e.target.value })}
              required
              disabled={!!editingAlias}
              fullWidth
              helperText="The SKU that is an alias (e.g. ALIAS-123)"
            />
            <TextField
              label="Primary SKU"
              value={aliasForm.primary_sku}
              onChange={(e) => setAliasForm({ ...aliasForm, primary_sku: e.target.value })}
              required
              fullWidth
              helperText="The real SKU this alias maps to (e.g. REAL-SKU-1)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAliasDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAlias}
            disabled={!aliasForm.sku || !aliasForm.primary_sku}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />

      {/* Production BOM editor (BOM tab on the product page) */}
      <ProductionBomEditorDialog
        open={prodBomEditor.open}
        onClose={() => setProdBomEditor((s) => ({ ...s, open: false }))}
        brandId={selectedBrand?.id}
        mode={prodBomEditor.mode}
        initialForm={prodBomEditor.initialForm}
        lockAssembly
        allowDeactivate
        onSaved={() => { setSuccess('Production BOM saved'); refreshProductDetail(); }}
        onDeactivated={() => { setSuccess('Production BOM deactivated'); refreshProductDetail(); }}
      />

      {/* Post-create quick-assign: add a new component to FG recipes in one go */}
      <AddToRecipesDialog
        open={!!assignRecipesFor}
        brandId={selectedBrand?.id}
        componentSku={assignRecipesFor?.sku}
        componentName={assignRecipesFor?.name}
        onClose={() => setAssignRecipesFor(null)}
        onAssigned={(result) => {
          const sku = assignRecipesFor?.sku;
          setAssignRecipesFor(null);
          const parts = [];
          if (result.added > 0) parts.push(`added to ${result.added} recipe${result.added === 1 ? '' : 's'}`);
          if (result.recipes_created > 0) parts.push(`first recipe created for ${result.recipes_created} product${result.recipes_created === 1 ? '' : 's'}`);
          if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
          const msg = `${sku} — ${parts.join(', ') || 'nothing changed'}`;
          setSuccess(msg);
          setTimeout(() => setSuccess((s) => (s === msg ? null : s)), 8000);
          loadData();
        }}
      />

      {productTools.dialogs}

      <BulkImportDialog
        open={cogsImportOpen}
        onClose={() => setCogsImportOpen(false)}
        entityLabel="COGS row"
        entityLabelPlural="COGS rows"
        mode={cogsImportMode}
        columnSpec={COGS_IMPORT_COLUMNS}
        sampleRows={COGS_IMPORT_SAMPLE}
        onSubmit={handleCogsBulkImport}
        helpText={
          cogsImportMode === 'update'
            ? `Round-trip: export ${selectedBrand?.name || 'this brand'}'s COGS, edit costs in Excel, re-upload. Matches by (sku, warehouse_code). Blank cells skip; __CLEAR__ explicitly empties.`
            : `Importing COGS rows for ${selectedBrand?.name || 'the selected brand'}. Required: sku + cogs. The same (sku, warehouse_code) replaces the existing cost — safe to use for both create and update.`
        }
      />

      <BulkImportDialog
        open={bundleImportOpen}
        onClose={() => setBundleImportOpen(false)}
        entityLabel="bundle component"
        entityLabelPlural="bundle components"
        mode="insert"
        columnSpec={BUNDLE_IMPORT_COLUMNS}
        sampleRows={BUNDLE_IMPORT_SAMPLE}
        onSubmit={handleBundleBulkImport}
        helpText={`One row per component. All rows sharing the same parent_sku become one bundle. Bundle-level fields (currency, bundle_overhead, warehouse_code) are taken from the FIRST row of each parent_sku group — leave blank on subsequent rows.`}
      />

      <BulkStatusUpdateDialog
        open={bulkStatusOpen}
        onClose={() => setBulkStatusOpen(false)}
        brand={selectedBrand}
        onApplied={() => loadData()}
        reloadError={error}
      />

      <LeadTimeOverrideDialog
        open={ltOverrideDialog.open}
        onClose={() => setLtOverrideDialog((s) => ({ ...s, open: false }))}
        brandId={selectedBrand?.id}
        sku={ltOverrideDialog.sku}
        regime={ltOverrideDialog.regime}
        currentComputed={ltOverrideDialog.computed}
        hasOverride={ltOverrideDialog.hasOverride}
        onSaved={loadData}
      />
    </Box>
  );
}

export default ProductCOGS;
